// Order detail — Phase 7.5.3.
//
// Layout: left column = order header (status, client, dates, terms, PO,
// intro) with inline-edit affordance for admin/client_rep/order_rep.
// Right column = line items table (read-only this PR; quantity + price
// editing lands in 7.5.3.b or 7.5.4 alongside add/remove + decoration UI).
//
// Pattern mirrors ClientDetailPage.tsx — same edit/cancel cycle, same
// optimistic mutation, same role gate, same brand palette. State machine
// transitions are NOT exposed in this PR — those keep going through the
// existing approve/reject/transition endpoints.
import { CopyToInvoiceButton, invoiceChoicesFor, type InvoiceTargets } from "@/components/invoices/CopyToInvoiceButton";
import { RemoveFromInvoiceButton } from "@/components/invoices/RemoveFromInvoiceButton";
import { removeFromInvoiceFor } from "@/lib/invoice-targets";
import { CustomProductForm, type CreatedCustomProduct } from "@/components/catalog/CustomProductForm";
import { Fragment, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Copy,
  CopyPlus,
  ExternalLink,
  FileText,
  Info,
  Lock,
  LockOpen,
  Pencil,
  Plus,
  Rows2,
  Rows4,
  Table2,
  Trash2,
  Truck,
  ChevronUp,
  ChevronDown,
  History,
} from "lucide-react";
import { OrderAddressModal } from "@/components/orders/OrderAddressModal";
import { OrderUrgencyToggles } from "@/components/orders/OrderUrgencyToggles";
import {
  type CloneSourceItem,
  type CloneTargetSubmit,
  CloneTargetModal,
} from "@/components/clone/CloneTargetModal";
import {
  type ConvertSelectionResult,
  type ConvertSourceItem,
  ConvertItemSelectionModal,
} from "@/components/convert/ConvertItemSelectionModal";
import {
  type SendRecipientInput,
  SendToCustomerModal,
} from "@/components/send/SendToCustomerModal";
import { SentEmailsCard } from "@/components/send/SentEmailsCard";
import { DeleteEntityAction } from "@/components/common/DeleteEntityAction";
import { PublicOrderPage } from "@/pages/PublicOrderPage";
import { ChargeQtyReadyContext } from "@/pages/OrderLineEditor";
import { SentCopiesModal } from "@/components/orders/SentCopiesModal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import { AddProductModal } from "@/components/catalog/AddProductModal";
import type { AddProductRow } from "@/components/catalog/AddProductModal";
import { ApiError, api } from "@/lib/api";
import { timeAgo } from "@/lib/utils";
import { useAuth } from "@/auth/AuthProvider";
import { OrderCommissionPanel } from "@/components/finance/OrderCommissionPanel";
import { orderStatusLabel, orderStatusTone } from "@/lib/orders";
import { canCreateEstimateAtStage } from "@/lib/projectStage";
import type { ProjectDetailResponse } from "@/pages/ProjectDetailPage";
import { VariantTableRow } from "@/components/orders/VariantTable";
import { SalesTaxAuto } from "@/components/orders/SalesTaxPanel";
import {
  SizeBreakdownEditor,
  fetchVariantOptions,
  variantOptionsQueryKey,
} from "@/components/orders/SizeBreakdownEditor";
import { LineBillsSection } from "@/components/orders/LineBillsSection";
import { NextActionCta } from "@/components/orders/NextActionCta";
import { LogoColourChecks } from "@/components/orders/LogoColourChecks";
import { shortShipTo } from "@/components/orders/shortShipTo";
import { isQuoteClientApproved } from "@/components/orders/OrderShippingTab";
import { ESTIMATE_LINE_PREFIX, ShippingEstimateModal } from "@/components/orders/ShippingEstimateModal";
import {
  deriveNextAction,
  shouldShowNextAction,
} from "@/components/orders/nextAction";
import {
  derivePoPreconditions,
  lineNeedsArtwork,
  detectDuplicateFeeEntry,
  // The CLIENT-FACING grouping function, used unchanged on the rep's estimate.
  // Deliberately NOT a third hand-rolled grouping — the second one was the bug
  // that could drop a product off a rep's own screen. The presentation BUILDER
  // needs its own (groupItemsForBuilder) because a rep creates blank sections
  // there and must see them; an ORDER has no section editor, so what the rep
  // sees here should be exactly what the client sees on /o/:uuid.
  groupLinesForDocument,
  isSalesOrderStage,
  isSoSignoffReachable,
  sectionChromeFor,
  zeroCostProductLineNames,
} from "@merchd/shared";
import { DocumentLineCard } from "@/components/documents/DocumentLineCard";
import { OrderLineEditModal } from "@/pages/OrderLineEditModal";
import { DocumentTotals } from "@/components/documents/DocumentTotals";
import type { DocCharge, DocSection } from "@/components/documents/types";
import {
  DocumentSectionBandRow,
  DocumentSectionCaption,
  DocumentSectionEditHeader,
  DocumentSectionHeading,
} from "@/components/documents/DocumentSection";
import {
  computeOrderTotals,
  lineTotalCents,
  lineAllInCostCents,
} from "@/components/documents/money";
import { MentionTextarea } from "@/components/notes/MentionTextarea";
import { effectiveMarginPct, lineCost, sellAtMargin } from "@/lib/line-money";

const EDIT_ROLES = ["admin", "client_rep", "order_rep"] as const;

// CEO 2026-08-04 supersedes the round-59 opener-only default: the send dialog now
// pre-fills the WHOLE email from GET /orders/:id/send-compose (rendered by the
// same server template the send route uses, including the Estimate vs Sales
// Order copy split) and every word of it is editable. No client-side default
// string — duplicating the copy here is the drift the compose endpoint prevents.

const TERMS_OPTIONS = [
  "net_15",
  "net_30",
  "net_45",
  "net_60",
  "net_90",
  "credit_card",
  "upfront",
] as const;
type TermsOption = (typeof TERMS_OPTIONS)[number];

const TERMS_LABELS: Record<TermsOption, string> = {
  net_15: "Net 15",
  net_30: "Net 30",
  net_45: "Net 45",
  net_60: "Net 60",
  net_90: "Net 90",
  credit_card: "Credit card",
  upfront: "Paid upfront",
};

// The order billing/shipping snapshot is rendered by snapshotAddress()
// (apps/api/src/services/orders-builder.ts) with these canonical keys:
// label / attention / street1 / street2 / city / stateCode / postalCode /
// countryCode. The legacy line1/state/country fields below are tolerated as
// a fallback so any older/imported snapshot still renders — but the FULL
// display reads the canonical keys first (fixes the "only city, zip" bug —
// the old card read line1/state/country, none of which the snapshot has).
interface AddressSnapshot {
  label?: string;
  companyName?: string;
  attention?: string;
  street1?: string;
  street2?: string;
  city?: string;
  stateCode?: string;
  postalCode?: string;
  countryCode?: string;
  // Legacy / alternate keys (older imports) — fallback only.
  name?: string;
  company?: string;
  line1?: string;
  line2?: string;
  state?: string;
  country?: string;
  phone?: string;
  email?: string;
  [k: string]: unknown;
}

interface OrderDetail {
  id: string;
  orderNumber: number;
  projectId: string;
  statusStage: string;
  statusState: string;
  orderDate: string;
  terms: string;
  customerPo: string | null;
  currency: string | null;
  /** Order-level tax code ("FL", "E"). Returned by GET /orders/:id; the
   *  document footer shows it against the tax row, commonsku-style. */
  taxCode: string | null;
  inHandsDate: string;
  introduction: string | null;
  requiresUpfrontPayment: boolean | null;
  /** 2026-07-20 — commonsku-style upfront payment. */
  upfrontType: string | null;
  upfrontAmount: string | null;
  ccFeePaidBy: "merchd" | "client" | null;
  /** Server-derived: 'not_required' | 'pending' | 'paid'. */
  upfrontPaymentStatus?: "not_required" | "pending" | "paid";
  /** Server-computed charge (base + card fee) in integer cents. */
  upfrontDueCents?: number;
  /** The 3% card fee in cents (0 when merch'd pays). */
  ccFeeCents?: number;
  /** The card-fee percent (tenant setting) — never hardcode 3%. */
  ccSurchargePct?: number;
  /** True when the client pays the card fee. */
  upfrontFeeIncluded?: boolean;
  /** Back-populated when a deposit/upfront invoice has been created for this
   *  order. Null = none yet. "View payment" links to it. */
  depositInvoiceId: string | null;
  publicShareUuid: string;
  billingAddressSnapshot: AddressSnapshot | null;
  shippingAddressSnapshot: AddressSnapshot | null;
  /** FK to the address each snapshot was rendered from — preselects the
   *  current address when the rep edits the block. Null on placeholders. */
  billingAddressId: string | null;
  shippingAddressId: string | null;
  hidePricing: boolean;
  /** Quote "Blank order" box (2026-10-14). null until the DB column exists. */
  blankOrder?: boolean | null;
  /** Flow's "Show the approval section" on the client's quote page (CEO
   *  2026-10-15). null until the DB column exists (then always shown). */
  showApproval?: boolean | null;
  /** The client's latest "Request a change" since the last send. */
  clientChangeRequest?: { note: string | null; by: string | null; at: string } | null;
  /** Billing / shipping with company + Attn filled in (2026-10-14). */
  billingAddressDisplay?: AddressSnapshot | null;
  shippingAddressDisplay?: AddressSnapshot | null;
  /** Internal shipping notes (2026-10-14) — staff-only. null until the DB column exists. */
  shippingNotes?: string | null;
  /** The quote's own internal notes ("" none); null until Jake's step. */
  internalNotes?: string | null;
  /** "+ Add cost" / Display-Subtract are switched on (Jake's DDL has run). */
  /** Charge Qty is typeable (scripts/push-charge-quantity.ts has run). */
  chargeQuantityReady?: boolean;
  itemCostsReady?: boolean;
  /** 2026-05-26 (PR #155) — per-order Terms & Conditions override. */
  termsAndConditions: string | null;
  /** 2026-06-01 (PR #154b cont.) — operator-set per-estimate defaults.
   *  Both percentages 0..100 as decimal-string. Server applies tax in
   *  the totals; AddProductModal reads margin to auto-price new lines.
   *  Null = today's behavior (no tax, no auto-price). */
  defaultTaxRate: string | null;
  defaultMarginPct: string | null;
  /** 2026-07-14 (snag #8 admin escape hatch) — active admin line-edit unlock
   *  window. When linesUnlockExpiresAt is in the future, line edits are
   *  permitted even at a locked stage. linesUnlockedByName is hydrated by
   *  GET /orders/:id for the banner. Null = locked. */
  linesUnlockExpiresAt: string | null;
  linesUnlockedByName: string | null;
  /** Estimate client sign-off. Non-null once the estimate is approved; carried
   *  forward by convert_to_so, so it is NOT the Sales-Order sign-off signal. */
  approvedAt: string | null;
  approvedByName: string | null;
  /** 2026-07-26 — DISTINCT Sales-Order sign-off (so_approved_*). Non-null once
   *  the SO is signed off (client or rep-offline). soApprovedByName is null for
   *  a rep-recorded offline sign-off. Gates the SO "Mark approved" affordance +
   *  the rep-side approved line on SO stages. */
  soApprovedAt: string | null;
  soApprovedByName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface OrderTotals {
  subtotal: string;
  tax: string;
  total: string;
  lineCount: number;
}

/** Row shape from /projects/:id/purchase-orders — used only to derive the
 *  shipped-stage NextAction (two-primary fix). Superset of deriveNextAction's
 *  DerivePoInput; `orderId` lets us filter to this order's POs. */
interface NextActionPoRow {
  id: string;
  poNumber: number;
  orderId: string;
  internalStage: string;
  sentAt: string | null;
  vendorId: string;
  vendorName: string;
  poType: string;
  shipFromVendorId: string | null;
}

interface LineVariant {
  id: string;
  color: string | null;
  size: string | null;
  material: string | null;
  quantity: number;
  unitCost: string | null;
  unitPrice: string | null;
}

interface LineDecoration {
  id: string;
  /** 'item_costs' = the line's "+ Add cost" holder, not an imprint location
   *  (Stephanie 2026-10-14). Absent/'location' = an imprint location. */
  kind?: "location" | "item_costs";
  imprintTypeId: string | null;
  /** FK to the line's attached artwork (line_item_artwork). Present ⇒ the line
   *  carries artwork — used by the client PO-prereq mirror (derivePoPreconditions). */
  artworkId?: string | null;
  /** Imprint location ("Centered on front") — the document's group header.
   *  This interface previously declared a `description` field the API has never
   *  returned; corrected 2026-07-19 against orders-builder's decorationSelect. */
  location: string | null;
  colorCount: number | null;
  /** Presentation→order carry columns (round 34) — returned by GET /orders/:id
   *  via decorationSelect; edited in the Imprint tab of OrderLineEditModal. */
  imprintType?: string | null;
  proofRequired?: string | null;
  /** Structured proof-type enum — the per-location PO artwork gate (T15) requires
   *  it. Returned by GET /orders/:id via decorationSelect (orders-builder.ts). */
  proofType?: "none" | "email_proof" | "digital_photo" | "pre_production" | null;
  logoName?: string | null;
  logoSize?: string | null;
  logoColor?: string | null;
  repeatLogo?: string | null;
  supplierNotes?: string | null;
  logoFileId?: string | null;
  /** Setup / run charges. Returned as of 2026-07-19 — they were write-only
   *  before (schema + POST existed, nothing read them back). */
  charges: DocCharge[];
}

interface LineItem {
  /** Copied onto another quote's invoice — billed there (Stephanie 2026-10-16). */
  billedOnInvoiceNumber?: number | null;
  id: string;
  lineNumber: number;
  serviceType: string;
  description: string | null;
  /** THE SKU (catalogue SKU, ATS-10001), joined live by GET /orders/:id. */
  catalogueSku?: string | null;
  /** The catalogue product (returned by lineSelect); null for custom lines. */
  productId?: string | null;
  // 2026-05-28 — product snapshot captured at line insert (name / sku /
  // image). Already returned by GET /orders/:id via lineSelect; the table
  // now renders the name instead of falling back to "(product)".
  productSnapshot: {
    name?: string;
    sku?: string;
    imageUrls?: string[] | Record<string, string>;
    /** Captured at line insert (from products.vendor_id). Its presence is the
     *  client mirror of the server's vendor_lookup_failed prereq. */
    vendorId?: string;
  } | null;
  /** Decoration path enum ("blank_no_decoration" | screen_print | … | null).
   *  A real decoration ⇒ artwork required (mirrors the server prereq). */
  decorationPath?: string | null;
  netCost: string;
  unitPrice: string;
  quantityTotal: number;
  position: number;
  /** Titled-section grouping, carried from the presentation at convert
   *  (packages/shared/src/section-carry.ts). NULL = ungrouped, which renders
   *  FIRST with no heading — so every estimate created before sections existed
   *  looks exactly as it did. */
  sectionId?: string | null;
  variants: LineVariant[];
  decorations: LineDecoration[];
}

interface OrderDetailResponse {
  order: OrderDetail;
  lines: LineItem[];
  /** lineId → the other invoice it's billed on. */
  billed_elsewhere?: Record<string, { invoiceId: string; invoiceNumber: number }>;
  /** Always present, `[]` when the order has none (GET /orders/:id). Carries
   *  nothing cost-bearing — id / title / position only. */
  sections?: DocSection[];
  client: { clientId: string; clientName: string; projectName: string } | null;
  totals?: OrderTotals;
  /** Server-computed: are line items still editable? Locked once a live FINAL
   *  invoice exists (CEO rule 2026-07-19). Never re-derive this client-side —
   *  the server is the enforcement point and must be the only definition. */
  lines_editable?: boolean;
  /** Whether a client-facing email was actually sent for this order.
   *  Derived server-side from email_sends. Keeps the status honest on reload
   *  — a link-only send-for-approval must never read as "Quote — Sent". */
  email_sent?: boolean;
  /** ONE Quote per project (CEO 2026-07-20). The project's existing
   *  Quote (excluding this order), or null. When set, this estimate's
   *  "Convert to Quote" button becomes a disabled-with-explanation state
   *  that points at copy-to-a-new-project — the sanctioned way to make another. */
  project_sales_order?: { id: string; order_number: number } | null;
}

interface EditableFields {
  customerPo: string;
  inHandsDate: string;
  terms: TermsOption;
  introduction: string;
}

interface PatchBody {
  customer_po?: string | null;
  in_hands_date?: string;
  terms?: TermsOption;
  introduction?: string | null;
  hide_pricing?: boolean;
  /** 2026-05-26 (PR #155) — per-order Terms & Conditions override.
   *  Empty string from the rep is interpreted as null (clears the
   *  override; PublicOrderPage falls back to the hardcoded default). */
  terms_and_conditions?: string | null;
  /** 2026-06-01 (PR #154b cont.) — operator-set per-estimate defaults.
   *  Both percentages 0..100 as decimal-strings; null clears. */
  default_tax_rate?: string | null;
  default_margin_pct?: string | null;
  /** 2026-07-20 — commonsku-style upfront payment. */
  requires_upfront_payment?: boolean;
  upfront_type?: "full" | "partial" | null;
  upfront_amount?: string | null;
  cc_fee_paid_by?: "merchd" | "client";
  /** 2026-07-21 — editable billing/shipping. Sending an id re-renders the
   *  order's snapshot from that saved address server-side. */
  billing_address_id?: string;
  shipping_address_id?: string;
}

export function OrderDetailPage() {
  // 2026-05-29 — rendered inside the project sub-nav at
  // /projects/:id/{estimate|sales-order}/:orderId, so the ORDER id is the
  // `orderId` param. (The standalone /orders/:id route redirects here, so
  // we never render without the nested param.) The order's own projectId
  // comes back in the payload for back/convert navigation.
  const params = useParams<{ id: string; orderId: string }>();
  const id = params.orderId ?? params.id ?? "";
  // Nested under ProjectDetailLayout's <Outlet>; read the project payload to
  // gate the in-context "New estimate" affordance (P2) to the same stages the
  // rule engine offers create_estimate. Optional-typed for the standalone
  // /orders/:id redirect edge.
  const projectCtx = useOutletContext<ProjectDetailResponse | undefined>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const detailQueryKey = useMemo(() => ["orders", id, "detail"] as const, [id]);

  const canEdit =
    user !== null && EDIT_ROLES.some((r) => user.roles.includes(r));
  // Admin-only line-edit unlock escape hatch (snag #8) — matches the
  // requireRole("admin") gate on POST /orders/:id/unlock-lines.
  const isAdmin = user !== null && user.roles.includes("admin");
  // Create PO is the sales→production handoff — admin / production_rep only
  // (matches the POST /orders/:id/pos route roles), distinct from edit roles.
  const canCreatePo =
    user !== null &&
    (user.roles.includes("admin") || user.roles.includes("production_rep"));

  const { data, isLoading, isError } = useQuery({
    queryKey: detailQueryKey,
    queryFn: () => api<OrderDetailResponse>(`/orders/${id}`),
    enabled: id.length > 0,
  });

  // Two-primary fix (ui review 2026-07-26): at `shipped`, Convert-to-Invoice is
  // primary ONLY when the derived NextAction is "complete"; otherwise the CTA
  // card owns the primary (its send_pos/add_tracking action) and Convert stays
  // outline. We read the SAME project POs query the CTA uses (shared cache key —
  // no extra network) so we can derive that state at this level. Fetched on ALL
  // CTA stages (E3) so scoped re-create (recreatableVendors) can surface whenever
  // a leg was cancelled — not just at shipped.
  const shippedProjectId = data?.order.projectId ?? "";
  const { data: shippedPosData } = useQuery({
    queryKey: ["project", shippedProjectId, "purchase-orders"],
    queryFn: () =>
      api<{ rows: NextActionPoRow[] }>(
        `/projects/${shippedProjectId}/purchase-orders`,
      ),
    enabled:
      shouldShowNextAction(data?.order.statusStage ?? "") &&
      shippedProjectId.length > 0,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<EditableFields | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const enterEdit = (): void => {
    if (!data?.order) return;
    const o = data.order;
    setForm({
      customerPo: o.customerPo ?? "",
      inHandsDate: o.inHandsDate,
      terms: (TERMS_OPTIONS.find((t) => t === o.terms) ?? "net_30") as TermsOption,
      introduction: o.introduction ?? "",
    });
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEdit = (): void => {
    setIsEditing(false);
    setForm(null);
    setSaveError(null);
  };

  useEffect(() => {
    if (!isEditing) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") cancelEdit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isEditing]);

  const buildPatchBody = (
    f: EditableFields,
    base: OrderDetail,
  ): PatchBody => {
    const body: PatchBody = {};
    const newPo = f.customerPo.trim();
    if (newPo !== (base.customerPo ?? "")) {
      body.customer_po = newPo.length > 0 ? newPo : null;
    }
    if (f.inHandsDate !== base.inHandsDate) body.in_hands_date = f.inHandsDate;
    if (f.terms !== base.terms) body.terms = f.terms;
    const newIntro = f.introduction.trim();
    if (newIntro !== (base.introduction ?? "")) {
      body.introduction = newIntro.length > 0 ? newIntro : null;
    }
    return body;
  };

  const validate = (f: EditableFields): string | null => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.inHandsDate)) {
      return "In-hands date must be YYYY-MM-DD.";
    }
    return null;
  };

  const navigate = useNavigate();
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showSentCopies, setShowSentCopies] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const cloneMutation = useMutation({
    mutationFn: async (next: CloneTargetSubmit) =>
      api<{
        new_order_id: string;
        new_order_number: number;
        source_order_number: number;
        new_project_id: string | null;
        lines_cloned: number;
        decorations_cloned: number;
      }>(`/orders/${id}/clone`, {
        method: "POST",
        body: JSON.stringify({
          new_project: {
            client_id: next.clientId,
            name: next.projectName,
          },
          selected_line_ids: next.selectedIds,
        }),
      }),
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setCloneError(body?.error ?? `Clone failed (${err.status}).`);
      } else {
        setCloneError("Clone failed. Try again or check your connection.");
      }
    },
    onSuccess: (result) => {
      setCloneError(null);
      setShowCloneModal(false);
      // The copy's project lists and counts (Stephanie 2026-11-02 sweep).
      void queryClient.invalidateQueries({ queryKey: ["merchd.projectDetail"] });
      void queryClient.invalidateQueries({ queryKey: ["project"] });
      navigate(`/orders/${result.new_order_id}`);
    },
  });

  // PR #140 — Convert Estimate to Quote. Visible on any quote-stage
  // estimate. The button opens the commonsku-style item/quantity picker
  // (ConvertItemSelectionModal, fed by the order's current lines); on confirm
  // it POSTs line_selections so the rep can drop/re-quantify lines before the
  // stage flip. An empty body still works (backend converts all lines as-is).
  const [convertError, setConvertError] = useState<string | null>(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const convertMutation = useMutation({
    mutationFn: async (payload?: {
      line_selections: { line_id: string; quantity: number }[];
    }) =>
      api<{
        order: { id: string; statusStage: string; statusState: string };
      }>(`/orders/${id}/convert-to-sales-order`, {
        method: "POST",
        ...(payload ? { body: JSON.stringify(payload) } : {}),
      }),
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as {
          error?: string;
          existing_order_number?: number;
        } | null;
        if (body?.error === "no_lines_selected") {
          setConvertError(
            "Pick at least one product to carry into the Quote.",
          );
          return;
        }
        // ONE Quote per project (CEO 2026-07-20). Two-tabs race: the
        // project already got its Quote while this modal was open.
        // Same friendly copy as the disabled-button hint; refetch so the page
        // catches up (button flips to the disabled-with-explanation state).
        if (body?.error === "project_already_has_sales_order") {
          const soNum = body.existing_order_number;
          setConvertError(
            `This project already has Quote${
              soNum ? ` #${soNum}` : ""
            } — one Quote per project. Use “Clone” to copy these items into a new project instead.`,
          );
          setShowConvertModal(false);
          void queryClient.invalidateQueries({ queryKey: detailQueryKey });
          return;
        }
        setConvertError(body?.error ?? `Convert failed (${err.status}).`);
      } else {
        setConvertError("Convert failed. Try again or check your connection.");
      }
    },
    onSuccess: () => {
      setConvertError(null);
      setShowConvertModal(false);
      // Refetch the order so the page reflects the new committed state (the
      // Convert button hides; a stale cache previously kept showing it, so a
      // second click hit "already committed/approved"). Also refresh the
      // project sub-nav so the Estimate/Quote badge counts update.
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
      void queryClient.invalidateQueries({
        queryKey: ["merchd.projectDetail"],
      });
      // Now committed → re-route through the order redirect, which lands
      // on the Quote detail inside the project sub-nav.
      navigate(`/orders/${id}`);
    },
  });

  // PR #150 (2026-05-26) — Convert Quote to Invoice. Visible on
  // statusStage ∈ {committed, in_production, shipped} (any active SO
  // stage pre-invoiced). Creates a final invoice and navigates to the
  // /finance/invoicing list — per-invoice detail page lands later.
  const [createPoError, setCreatePoError] = useState<string | null>(null);
  const [poArtworkOverride, setPoArtworkOverride] = useState(false);
  // $0-cost free-goods confirm (2026-07-26). Non-null (even empty) = the
  // pre-create confirm is showing: a PO with a $0 product line would tell the
  // supplier the goods are free, so the rep must explicitly confirm before we
  // mint it (skip_zero_cost_check). Holds the offending line names for the copy.
  const [zeroCostNames, setZeroCostNames] = useState<string[] | null>(null);
  // D3 — the created POs, shown as an explicit result (naming supplier +
  // decoration POs) instead of a silent redirect when a decorator two-leg mints
  // more than one PO from one Create-POs click.
  const [createdPos, setCreatedPos] = useState<
    Array<{ id: string; poNumber: number; poType: string }>
  >([]);
  // G3 — POs orphaned by a scoped re-derive (superseded decoration legs),
  // flagged for review; shown in the create-result panel at the moment of action.
  const [orphanedPos, setOrphanedPos] = useState<
    Array<{ id: string; poNumber: number }>
  >([]);
  const [sendApprovalMsg, setSendApprovalMsg] = useState<string | null>(null);
  // Copy-link affordance (item 8) — mirrors PresentationDetailPage and
  // PurchaseOrderDetailPage, which both had one. This page only had Preview
  // (which OPENS the URL, it doesn't copy it), so the send dialog's
  // compose-failure fallback advice — "close this and use Copy link" — was
  // literally false here. Same timed-copy idiom as the sibling pages.
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  const shareLinkCopied = copiedAt !== null && Date.now() - copiedAt < 2000;
  useEffect(() => {
    if (copiedAt === null) return;
    const t = setTimeout(() => setCopiedAt(null), 2100);
    return () => clearTimeout(t);
  }, [copiedAt]);
  // Missing-link soft gate (CEO 2026-08-04) — peach NOTICE + armed second click,
  // mirroring the PO $0-decoration confirm exactly.
  const [sendModalNotice, setSendModalNotice] = useState<string | null>(null);
  const [missingLinkConfirmPending, setMissingLinkConfirmPending] =
    useState(false);
  // Set when send-for-approval hit 409 no_client_contact — drives the
  // add-contact / share-link-only prompt instead of a plain error line.
  const [noContactPrompt, setNoContactPrompt] = useState(false);
  // Drives the recipient-picker modal (the "Send to customer" affordance).
  const [showSendModal, setShowSendModal] = useState(false);

  // 🔴 Who earned NOTHING on the invoice that was just created. The commission
  // engine reports this, and until it is rendered here nobody sees it: the rep
  // converting the order may not have the admin/finance role that Commission
  // Settings requires, and this request is the last synchronous moment anyone
  // is looking at the transaction. After it, the only trace is a server log.
  const [commissionWarnings, setCommissionWarnings] = useState<
    Array<{ code: string; repId: string; scenario: string; message: string }>
  >([]);

  const convertToInvoiceMutation = useMutation({
    mutationFn: async () =>
      api<{
        invoiceId: string;
        invoiceNumber: number;
        commissionWarnings?: Array<{
          code: string;
          repId: string;
          scenario: string;
          message: string;
        }>;
      }>(`/orders/${id}/convert-to-invoice`, { method: "POST" }),
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; invoiceId?: string } | null;
        if (body?.error === "invoice_already_exists") {
          setConvertError(
            "An invoice already exists for this order — open Finance > Invoices to find it.",
          );
          return;
        }
        if (body?.error === "order_has_no_lines") {
          setConvertError(
            "This order has no line items — add at least one before invoicing.",
          );
          return;
        }
        if (body?.error === "invoice_requires_sales_order") {
          setConvertError(
            "This quote needs to be approved first — a final invoice can only be created from an approved quote.",
          );
          return;
        }
        if (body?.error === "logo_colours_unconfirmed") {
          setConvertError(
            (body as { message?: string }).message ??
              "A logo has more colors than the website charged for — confirm the upcharge with the client first.",
          );
          return;
        }
        setConvertError(body?.error ?? `Convert failed (${err.status}).`);
      } else {
        setConvertError(
          "Convert to invoice failed. Try again or check your connection.",
        );
      }
    },
    onSuccess: (res) => {
      setConvertError(null);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
      void queryClient.invalidateQueries({
        queryKey: ["merchd.projectDetail"],
      });
      // The project's Invoice tab list, so the new invoice shows without a
      // reload (Stephanie 2026-11-02: it said "No invoices yet" after converting).
      if (data?.order.projectId) {
        void queryClient.invalidateQueries({ queryKey: ["project", data.order.projectId, "invoices"] });
      }
      const warnings = res?.commissionWarnings ?? [];
      const pid = data?.order.projectId;
      if (warnings.length > 0) {
        // 🔴 HOLD THE REDIRECT. Navigating straight to the invoice would carry
        // the rep past the only moment this is visible — the same silent zero,
        // one screen later. Mirrors the createdPos / orphanedPos result panel
        // below, which exists for exactly this reason on the two-leg PO mint.
        setCommissionWarnings(warnings);
        return;
      }
      navigate(pid ? `/projects/${pid}/invoice` : "/projects");
    },
  });

  // Convert a quote that was never sent straight to an invoice (Stephanie
  // 2026-11-02: "the client sent me a PO already so they don't need the quote,
  // only the invoice"). Records the approval (which starts the order, as any
  // approval does), then makes the invoice with the same step as "Convert to
  // Invoice". Nothing is emailed. Upfront-payment quotes don't get this.
  const [confirmInvoiceNow, setConfirmInvoiceNow] = useState(false);
  const invoiceNowMutation = useMutation({
    mutationFn: async () => {
      await api(`/orders/${id}/mark-approved`, { method: "POST", body: JSON.stringify({ note: "Client sent their own PO — invoiced without sending the quote." }) });
    },
    onSuccess: () => {
      setConfirmInvoiceNow(false);
      setConvertError(null);
      convertToInvoiceMutation.mutate();
    },
    onError: (err) => {
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      setConvertError(
        body?.error === "already_approved"
          ? "This quote is already approved — use Convert to Invoice."
          : "We couldn't approve this quote, so no invoice was made. Try again.",
      );
    },
  });

  // Mark approved (2026-07-26) — record an offline / verbal client approval.
  // SECONDARY affordance (never the primary CTA): applies the same
  // client_approved sign-off with actor=staff. Refetch on success so the button
  // disappears (approvedAt is now set) + the badge updates. Does NOT gate
  // conversion (#208) — Convert stays available regardless.
  const [markApprovedError, setMarkApprovedError] = useState<string | null>(
    null,
  );
  const markApprovedMutation = useMutation({
    mutationFn: async () =>
      api<{ order: { statusStage: string; statusState: string } }>(
        `/orders/${id}/mark-approved`,
        { method: "POST", body: JSON.stringify({}) },
      ),
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        if (body?.error === "already_approved") {
          setMarkApprovedError("This order is already approved.");
          return;
        }
        if (body?.error === "upfront_payment_required") {
          setMarkApprovedError(
            "This Quote requires an upfront payment — collect it via the Approve & Pay link instead.",
          );
          return;
        }
        setMarkApprovedError(body?.error ?? `Couldn't mark approved (${err.status}).`);
      } else {
        setMarkApprovedError("Couldn't mark approved. Try again.");
      }
    },
    onSuccess: () => {
      setMarkApprovedError(null);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
      void queryClient.invalidateQueries({
        queryKey: ["merchd.projectDetail"],
      });
    },
  });

  // Create PO(s) — sales→production handoff. Derives one PO per vendor from
  // this committed Quote via POST /orders/:id/pos (engine already
  // exists). Prereqs (no shipping address / missing artwork) surface as a
  // 409 with a `reason` we map to a clear message + an override.
  const createPoMutation = useMutation({
    mutationFn: async (opts: {
      skipArtwork?: boolean;
      skipZeroCost?: boolean;
      /** E3 — scoped re-create: only this vendor group's missing leg. */
      onlyVendorId?: string;
    } = {}) =>
      api<{
        pos: Array<{
          po: { id: string; poNumber: number; poType: string };
          line_count: number;
        }>;
        orphaned: Array<{ id: string; poNumber: number }>;
      }>(
        `/orders/${id}/pos`,
        {
          method: "POST",
          body: JSON.stringify({
            ...(opts.skipArtwork ? { skip_artwork_check: true } : {}),
            ...(opts.skipZeroCost ? { skip_zero_cost_check: true } : {}),
            ...(opts.onlyVendorId ? { only_vendor_id: opts.onlyVendorId } : {}),
          }),
        },
      ),
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; reason?: string } | null;
        if (body?.reason === "missing_artwork" && data?.order.blankOrder === false) {
          // Not a blank order → the logo is required, no "anyway".
          setCreatePoError(`Logo needed — ${body.error ?? "add the logo"}. Add it, or tick Blank order if there's no logo.`);
          return;
        }
        if (body?.reason === "missing_artwork") {
          setPoArtworkOverride(true);
          // Surface the server's precise per-location detail VERBATIM (body.error
          // = formatMissingArtworkLocations, e.g. "Tote — Left Chest (logo file,
          // proof type)") so the rep knows exactly which location + fields to fix
          // — not a generic "some items" toast that drops the diagnosis.
          setCreatePoError(
            body.error
              ? `Artwork specs needed — ${body.error}. Add them first, or create the PO anyway.`
              : "Some items don't have artwork specs yet. Add artwork first, or create the PO anyway.",
          );
          return;
        }
        if (body?.reason === "no_shipping_address") {
          setCreatePoError(
            "Add a shipping address to this order first, then create the PO.",
          );
          return;
        }
        // Server backstop for the $0-cost free-goods guard (the client confirm
        // was bypassed / raced). Re-open the confirm rather than a dead error —
        // the rep can still proceed with an explicit skip_zero_cost_check.
        if (body?.reason === "zero_cost") {
          setZeroCostNames([]);
          return;
        }
        setCreatePoError(body?.error ?? `Create PO failed (${err.status}).`);
      } else {
        setCreatePoError("Create PO failed. Try again or check your connection.");
      }
    },
    onSuccess: (res) => {
      setCreatePoError(null);
      setPoArtworkOverride(false);
      setZeroCostNames(null);
      // Refresh the project PO list so the scoped-recreate panel (E3) reflects
      // the newly-minted leg.
      void queryClient.invalidateQueries({
        queryKey: ["project", data?.order.projectId ?? "", "purchase-orders"],
      });
      const pid = data?.order.projectId;
      const orphaned = res.orphaned ?? [];
      if (res.pos.length === 1 && orphaned.length === 0) {
        // A single PO, nothing flagged: go straight to it (no ambiguity to name).
        // In-project PO detail (2026-07-25) so the sub-nav chrome stays put;
        // top-level fallback only if the order has no project.
        navigate(
          pid
            ? `/projects/${pid}/pos/${res.pos[0]!.po.id}`
            : `/pos/${res.pos[0]!.po.id}`,
        );
      } else {
        // D3 — 2+ POs, OR any orphaned PO (G3): name the created POs explicitly
        // and flag the orphaned ones, at the moment of action.
        setCreatedPos(res.pos.map((p) => p.po));
        setOrphanedPos(orphaned);
      }
    },
  });

  // 2026-07-20 — the rep-facing deposit-invoice form is RETIRED in
  // favour of commonsku-style upfront payment (the client approves + pays on
  // /o/:uuid). The POST /orders/:id/deposit-invoice route + createDepositInvoice
  // service stay intact (dormant): approve-and-pay creates the deposit-type
  // invoice internally, and any existing deposit invoices still render on the
  // invoice pages. See UpfrontPaymentPanel below.

  // Send to customer — emails the client (and any extra recipients the rep
  // picks — CEO "copy his boss") the share link. On an Estimate (quote/draft)
  // this moves it draft → sent; on a Quote (committed/in_production) it
  // records the send WITHOUT a state change (the SO stays approved). Stays on
  // this page. `recipients` omitted → the single-primary-contact fallback.
  const sendApprovalMutation = useMutation({
    mutationFn: async (opts?: {
      publishLinkOnly?: boolean;
      recipients?: SendRecipientInput[];
      /** The rep's FULL edited email (CEO 2026-08-04) — replaces the template. */
      bodyHtml?: string;
      /** Second click on the missing-link soft gate → send anyway. */
      skipMissingLinkCheck?: boolean;
      /** "Send me a copy" (Stephanie 2026-10-26). */
      sendCopyToMe?: boolean;
      /** The rep's own subject line (Stephanie 2026-10-27). */
      subject?: string;
    }) =>
      api<{
        public_url: string;
        email_sent: boolean;
        recipients?: string[];
        mode?: string;
      }>(`/orders/${id}/send-for-approval`, {
        method: "POST",
        body: JSON.stringify(
          opts?.publishLinkOnly
            ? { publish_link_only: true }
            : opts?.recipients
              ? {
                  recipients: opts.recipients,
                  // The rep's WHOLE email. Sanitised server-side to the strict
                  // allowlist before it is stored or sent.
                  ...(opts.bodyHtml !== undefined
                    ? { body_html: opts.bodyHtml }
                    : {}),
                  ...(opts.skipMissingLinkCheck
                    ? { skip_missing_link_check: true }
                    : {}),
                  ...(opts.sendCopyToMe ? { send_copy_to_me: true } : {}),
                  ...(opts.subject ? { subject: opts.subject } : {}),
                }
              : {},
        ),
      }),
    onError: (err) => {
      // 409 no_client_contact is a decision point, not a plain error —
      // surface the add-contact / share-link-only prompt (mirrors the
      // presentation send flow).
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        (err.body as { error?: string } | null)?.error === "no_client_contact"
      ) {
        setSendApprovalMsg(null);
        setShowSendModal(false);
        setNoContactPrompt(true);
        return;
      }
      // Missing-link soft gate (409). Keep the dialog OPEN so the rep's
      // recipients + body survive, arm the skip flag, and route the copy through
      // the PEACH notice slot — a confirm must never wear failure styling.
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        (err.body as { reason?: string } | null)?.reason ===
          "missing_document_link"
      ) {
        setMissingLinkConfirmPending(true);
        setSendApprovalMsg(null);
        setSendModalNotice(
          (err.body as { message?: string } | null)?.message ??
            "This email doesn't include the document link — the client won't be able to open it. Send anyway?",
        );
        return;
      }
      setNoContactPrompt(false);
      setSendApprovalMsg(
        err instanceof ApiError
          ? // Prefer the plain-text `message` (e.g. the custom_message zod
            // reason: "Plain text only — please remove any HTML tags.") over the
            // bare error code so a validation failure isn't a dead-end.
            ((err.body as { message?: string; error?: string } | null)?.message ??
              (err.body as { error?: string } | null)?.error ??
              `Send failed (${err.status}).`)
          : "Send for approval failed. Try again.",
      );
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ["sent-emails"] });
      setNoContactPrompt(false);
      setShowSendModal(false);
      setSendModalNotice(null);
      setMissingLinkConfirmPending(false);
      const who =
        res.recipients && res.recipients.length > 0
          ? res.recipients.join(", ")
          : null;
      setSendApprovalMsg(
        res.email_sent
          ? who
            ? `Sent to ${who}.`
            : "Sent — the client was emailed the link."
          : "Link shared — no email sent. Copy the link to give it to the client.",
      );
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });

  // Admin escape hatch (snag #8) — open / close a 30-min line-edit unlock
  // window on a locked or closed order. Both actions are audited server-side.
  const [unlockMsg, setUnlockMsg] = useState<string | null>(null);
  const unlockLinesMutation = useMutation({
    mutationFn: async () =>
      api<{ unlock_expires_at: string | null }>(
        `/orders/${id}/unlock-lines`,
        { method: "POST" },
      ),
    onError: (err) => {
      setUnlockMsg(
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ??
            `Unlock failed (${err.status}).`)
          : "Unlock failed. Try again.",
      );
    },
    onSuccess: () => {
      setUnlockMsg(null);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });
  const relockLinesMutation = useMutation({
    mutationFn: async () =>
      api<{ unlock_expires_at: null }>(`/orders/${id}/relock-lines`, {
        method: "POST",
      }),
    onError: (err) => {
      setUnlockMsg(
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ??
            `Re-lock failed (${err.status}).`)
          : "Re-lock failed. Try again.",
      );
    },
    onSuccess: () => {
      setUnlockMsg(null);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });

  // Which product lines need colors/sizes (Stephanie 2026-10-14: POs can't be
  // created without them). Same query as the quote's warning.
  const { data: poVariantOptions } = useQuery({
    queryKey: [
      ...variantOptionsQueryKey(id ?? ""),
      (data?.lines ?? []).map((l) => `${l.id}:${l.productId ?? ""}`).join(","),
    ],
    queryFn: () => fetchVariantOptions(id ?? ""),
    enabled: !!id && !!data,
    staleTime: 5 * 60_000,
  });

  // Quote "Blank order" box (Stephanie 2026-10-14).
  const blankOrderMutation = useMutation({
    mutationFn: (blank: boolean) =>
      api<{ blank_order: boolean }>(`/orders/${id}/blank-order`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ blank_order: blank }),
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
      void queryClient.invalidateQueries({ queryKey: ["project"] });
    },
  });

  // Client quote page: "Show the approval section" (CEO 2026-10-15).
  const showApprovalMutation = useMutation({
    mutationFn: (show: boolean) =>
      api<{ show_approval: boolean }>(`/orders/${id}/show-approval`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ show_approval: show }),
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (body: PatchBody) => {
      return api<{ order: OrderDetail }>(`/orders/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: detailQueryKey });
      const prior = queryClient.getQueryData<OrderDetailResponse>(detailQueryKey);
      if (prior?.order) {
        queryClient.setQueryData<OrderDetailResponse>(detailQueryKey, {
          ...prior,
          order: {
            ...prior.order,
            ...(body.customer_po !== undefined
              ? { customerPo: body.customer_po }
              : {}),
            ...(body.in_hands_date !== undefined
              ? { inHandsDate: body.in_hands_date }
              : {}),
            ...(body.terms !== undefined ? { terms: body.terms } : {}),
            ...(body.introduction !== undefined
              ? { introduction: body.introduction }
              : {}),
          },
        });
      }
      return { prior };
    },
    onError: (err, _body, context) => {
      if (context?.prior !== undefined) {
        queryClient.setQueryData(detailQueryKey, context.prior);
      }
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        const body = err.body as { error?: string } | null;
        setSaveError(body?.error ?? "Save failed. Please review the form.");
      } else {
        setSaveError("Save failed. Try again or check your connection.");
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
      setIsEditing(false);
      setForm(null);
      setSaveError(null);
    },
  });

  // 2026-05-28 (#154b) — direct line editing on the estimate. Add product
  // opens the catalog modal; add service drops a blank service line the
  // rep edits inline. Both invalidate the order so lines + totals refresh.
  const [showAddProduct, setShowAddProduct] = useState(false);
  // Floating "+ Add product" (Stephanie 2026-10-16) once the top one scrolls away.
  const [showShippingEstimate, setShowShippingEstimate] = useState(false);
  // Manual shipping (Stephanie 2026-10-13: "we won't always use the estimator
  // and we need a way to enter the net cost and sell cost manually").
  const [manualShipping, setManualShipping] = useState<null | { description: string; cost: string; price: string; error: string | null }>(null);
  // The right-hand Client total panel; LinesTable portals the totals into it.
  const [totalsEl, setTotalsEl] = useState<HTMLDivElement | null>(null);
  // Which category a newly-added product should land under, when the rep opened
  // the picker from a category's own `+` rather than the toolbar. NULL = the
  // plain Add product button, which files ungrouped exactly as it always has.
  const [addTargetSectionId, setAddTargetSectionId] = useState<string | null>(
    null,
  );
  // Partial-success notice from the add→file path. Not an error: the product
  // IS on the estimate. See addRow.
  const [lineNote, setLineNote] = useState<string | null>(null);

  // 2026-06-01 (PR #154b cont.) — operator can pick the service_type at
  // add time so a Shipping line populates the /o/:uuid Shipping totals
  // row immediately, no inline retype needed. Default placeholder uses
  // the type's friendly label as the description.
  const addServiceMutation = useMutation({
    mutationFn: (serviceType: ServiceLineType) =>
      api(`/orders/${id}/lines`, {
        method: "POST",
        body: JSON.stringify({
          service_type: serviceType,
          description: SERVICE_LINE_DESCRIPTIONS[serviceType],
          unit_price: "0",
          quantity_total: 1,
        }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });

  const addManualShipping = useMutation({
    mutationFn: (v: { description: string; cost: string; price: string }) =>
      api(`/orders/${id}/lines`, {
        method: "POST",
        body: JSON.stringify({
          service_type: "shipping",
          description: v.description,
          net_cost: v.cost,
          unit_price: v.price,
          quantity_total: 1,
        }),
      }),
    onSuccess: () => {
      setManualShipping(null);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
    onError: (err) =>
      setManualShipping((m) =>
        m && { ...m, error: err instanceof ApiError ? "Couldn't add the shipping line. Check the amounts and try again." : "Couldn't add the shipping line. Check your connection and try again." },
      ),
  });

  if (id === "") {
    return (
      <div className="mx-auto max-w-none">
        <p className="text-sm text-rust-600">Missing order id in URL.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-none">
        <p className="text-sm text-brown-300">Loading order…</p>
      </div>
    );
  }

  if (isError || !data?.order) {
    return (
      <div className="mx-auto max-w-none">
        <p className="text-sm text-rust-600">
          Could not load this order.
        </p>
        <Link
          to="/projects"
          className="mt-4 inline-flex items-center gap-1 text-sm text-brown-400 hover:text-brown-500"
        >
          <ArrowLeft className="h-4 w-4" /> Back to projects
        </Link>
      </div>
    );
  }

  const order = data.order;
  const client = data.client;
  const lines = data.lines.map((l) => ({ ...l, billedOnInvoiceNumber: data.billed_elsewhere?.[l.id]?.invoiceNumber ?? null }));

  // Add-a-product handlers for the shared <AddProductModal>. Kept ON THE PAGE
  // (not inside the shared modal) so the estimate/SO retain their resolve →
  // margin → POST /orders/:id/lines contract while the modal owns the shell,
  // filter row, and multi-add UX. `opts.qty` is the modal's Qty-per-add value.
  const orderId = id;
  const defaultMarginPct = order.defaultMarginPct;
  // A custom product just created from the add window → a line on this
  // estimate / quote. Cost and sell price are exactly what the rep typed;
  // blank stays blank (priced on the document), never a guessed number. A
  // default margin fills the sell price from a typed cost, like catalogue adds.
  async function addCustomLine(p: CreatedCustomProduct): Promise<string | null> {
    // The quote's default margin, else 40% (Stephanie 2026-10-26).
    const sell = p.sellPrice ?? (p.cost !== null ? sellAtMargin(p.cost, effectiveMarginPct(defaultMarginPct)) : null);
    const created = await api<{ line?: { id?: string } }>(`/orders/${orderId}/lines`, {
      method: "POST",
      body: JSON.stringify({
        service_type: "product",
        product_id: p.product_id,
        product_snapshot: {
          snapshotAt: new Date().toISOString(),
          name: p.name,
          sku: p.sku,
          imageUrls: [],
          brand: null,
          supplierName: p.supplierName,
          ...(p.description ? { description: p.description } : {}),
        },
        // The line shows its own description (Stephanie 2026-10-14: the one
        // typed in the custom-product form was missing from the quote).
        ...(p.description ? { description: p.description } : {}),
        ...(p.cost !== null ? { net_cost: p.cost } : {}),
        ...(sell !== null ? { unit_price: sell } : {}),
      }),
    });
    const newLineId = created?.line?.id;
    if (addTargetSectionId !== null && typeof newLineId === "string") {
      try {
        await api(`/orders/${orderId}/lines/${newLineId}/section`, {
          method: "PATCH",
          body: JSON.stringify({ section_id: addTargetSectionId }),
        });
      } catch {
        return "Product added, but it could not be filed under that category.";
      }
    }
    return null;
  }

  async function addRow(
    row: AddProductRow,
    opts: { qty: string },
  ): Promise<void> {
    // MOQ autofill: a blank qty box means "use the supplier minimum" — we
    // OMIT quantity_total so the server fills each product's own MOQ. Only
    // an explicitly-typed number overrides it.
    const trimmedQty = opts.qty.trim();
    const explicitQuantity =
      trimmedQty.length > 0
        ? Math.max(0, Math.floor(Number(trimmedQty) || 0))
        : null;
    // Net cost is the catalog wholesale price. 🔴 entryPiecePrice FIRST
    // (2026-08-11, GM Stephanie): the `lowest*` figures are the MINIMUM across
    // every quantity bracket — the cost at the LARGEST quantity the supplier
    // publishes — so seeding from them quoted the volume price to a rep adding
    // a hundred pieces. Production EMT BST publishes 100-299 @ 1.170 /
    // 300-499 @ 1.080 / 500+ @ 0.990, and this line seeded 0.990, which the
    // margin step below grossed to 1.65 — exactly the "$0.99/ea - $1.65/sell"
    // she reported. The lowest* values remain the fallback for products with no
    // bracket grid. Captured on the line so margin is computable later
    // regardless of how unit_price was set.
    const rawCost =
      row.entryPiecePrice ??
      row.lowestDecoratedPiecePrice ??
      row.lowestBlankPiecePrice ??
      "0";
    // The line takes a cost to 3 decimals and a sell price to the cent
    // (Stephanie 2026-10-26: "HTTP 400" adding a $2.625 product with no
    // default margin — the sell price was the raw cost).
    const netCost = lineCost(rawCost);
    // 2026-06-01 (PR #154b cont.) — when the rep has set a default margin on
    // the estimate, seed the sell price from cost instead of using the catalog
    // price directly. Falls back to today's behavior when no margin is set.
    // Priced at the quote's default margin, else 40% — never at cost
    // (Stephanie 2026-10-26: "put the default margin as 40%").
    const unitPrice = sellAtMargin(netCost, effectiveMarginPct(defaultMarginPct));
    // Resolve the preferred supplier product id so the line links back to the
    // catalog (matches the presentation→convert lines). Snapshot-only fallback
    // if the product has no offers.
    let productId: string | null = null;
    // The supplier's description comes with the product (Stephanie
    // 2026-10-26: "the description from the vendor has disappeared").
    let vendorDescription: string | null = null;
    try {
      const resp = await api<{
        product: {
          description?: string | null;
          offers: Array<{ isPreferred: boolean; supplierProductId: string; description?: string | null }>;
        };
      }>(`/storefront/products/${row.id}`);
      const offer = resp.product.offers.find((o) => o.isPreferred) ?? resp.product.offers[0];
      vendorDescription = resp.product.description?.trim() || offer?.description?.trim() || null;
      const preferred = resp.product.offers.find((o) => o.isPreferred);
      productId =
        preferred?.supplierProductId ??
        resp.product.offers[0]?.supplierProductId ??
        null;
    } catch {
      productId = null;
    }
    // Throws on failure — the shared modal catches it, shows the banner, and
    // leaves the row un-added (its addedCount is only bumped on success).
    const created = await api<{ line?: { id?: string } }>(
      `/orders/${orderId}/lines`,
      {
        method: "POST",
        body: JSON.stringify({
          service_type: "product",
          product_id: productId,
          product_snapshot: {
            snapshotAt: new Date().toISOString(),
            name: row.name,
            sku: row.supplierProductNumber,
            imageUrls: row.primaryImageUrl ? [row.primaryImageUrl] : [],
            brand: row.brand,
            supplierName: row.supplierName,
            ...(vendorDescription ? { description: vendorDescription } : {}),
          },
          ...(vendorDescription ? { description: vendorDescription } : {}),
          net_cost: netCost,
          unit_price: unitPrice,
          // Omit when blank → server autofills the supplier MOQ.
          ...(explicitQuantity !== null
            ? { quantity_total: explicitQuantity }
            : {}),
        }),
      },
    );
    // Opened from a category's own `+`? File it under that heading.
    //
    // Deliberately a SECOND call to the existing assign route rather than a
    // `section_id` on the create body: that route is where section ownership is
    // validated (the category must belong to THIS order), and duplicating the
    // check into the create path would mean two places to get it wrong. The
    // create is already committed if this fails, so the product is never lost —
    // worst case it lands ungrouped, which is the safe direction and visible.
    const newLineId = created?.line?.id;
    if (addTargetSectionId !== null && typeof newLineId === "string") {
      try {
        await api(`/orders/${orderId}/lines/${newLineId}/section`, {
          method: "PATCH",
          body: JSON.stringify({ section_id: addTargetSectionId }),
        });
      } catch {
        // SWALLOWED ON PURPOSE, and this is the important bit. The product is
        // already on the estimate — only the filing failed. Letting this throw
        // would surface the modal's blanket "Couldn't add the product" and
        // leave addedCount un-incremented, so a rep would reasonably retry and
        // put a SECOND billable line on a priced document (estimate adds allow
        // repeat-adds by design — there is no dedupe here). Landing ungrouped
        // is the safe direction and it is visible: the product shows under
        // "Also included" and can be dragged or re-filed.
        setLineNote(
          "Product added, but it couldn't be filed under that category — it's on the quote, ungrouped.",
        );
      }
    }
    void queryClient.invalidateQueries({ queryKey: detailQueryKey });
  }

  // Soft double-entry guardrail (packages/shared/duplicate-fee.ts): the same
  // rush/setup/other fee can be entered BOTH as a service line AND as a decoration
  // charge — both feed totals + booked margin. When both are present we show a
  // one-line peach nudge near the table. Purely informational: it NEVER blocks
  // a mutation or disables a control.
  const duplicateFee = detectDuplicateFeeEntry(lines);
  // ONE Quote per project (CEO 2026-07-20). When the project already
  // carries a Quote, this estimate can't become a second one — the
  // sanctioned path is copy-to-a-new-project (the "Clone" button below).
  const projectSalesOrder = data.project_sales_order ?? null;
  // Lines are editable through the Quote stage (operator 2026-06-11:
  // the Quote is the final approval stage, so items must be transparent
  // and editable). Editable at quote (Estimate) + committed (Quote);
  // once production starts (in_production+), lines lock.
  const stageLinesEditable =
    order.statusStage === "quote" || order.statusStage === "committed";
  // Admin escape hatch (snag #8) — a live unlock window keeps lines editable
  // even at a locked/closed stage. Mirrors the server guard's expiry check.
  const linesUnlocked =
    order.linesUnlockExpiresAt !== null &&
    new Date(order.linesUnlockExpiresAt).getTime() > Date.now();
  // Server decides (data.lines_editable). The old client-side stage rule is the
  // fallback only for an older payload that predates the flag.
  const serverSaysEditable = data?.lines_editable;
  const linesEditable =
    canEdit &&
    ((serverSaysEditable ?? stageLinesEditable) || linesUnlocked);
  // 2026-07-20 — Hide-totals is an ESTIMATE-only control (CEO): a Quote
  // never shows the "Hide totals" toggle. Server still accepts the field.
  const isEstimate = order.statusStage === "quote";
  // Stage-aware preview label (CEO 2026-07-24). The Preview button opens the
  // public /o/:uuid share page, so its noun must match what that page renders.
  // The Estimate-vs-Sales-Order predicate lives in ONE place —
  // packages/shared/src/document-label.ts's isSalesOrderStage — shared by this
  // button and the public page (PublicOrderPage's deriveDocLabel is built on the
  // SAME predicate), so the two surfaces can never disagree: a committed-or-later
  // order is a Quote, only quote is an Estimate. We keep the "Preview
  // Estimate"/"Preview Quote" strings inline (rather than composing them
  // from deriveDocLabel) so the sibling-owned qa-public-order-view gate — which
  // asserts the literal "Preview Estimate" is present here — stays green.
  const isSalesOrderView = isSalesOrderStage(order.statusStage);
  // One document name at every stage (CEO 2026-10-15).
  const previewDocLabel = "Preview Quote";
  // Upfront-payment panel visibility: settable/visible for any order that
  // isn't yet financially closed. Reps can PRE-SET upfront terms on the
  // estimate (quote) — the client approves + pays on /o/:uuid before it ever
  // becomes a Quote — and adjust through the active SO stages. Once the
  // order is invoiced/closed/cancelled the terms are moot, so the panel hides.
  // (commonsku sets this on the SO; we surface it from quote so the client's
  // Approve & Pay works on the estimate share page.)
  const upfrontPanelVisible =
    order.statusStage !== "invoiced" &&
    order.statusStage !== "closed" &&
    order.statusStage !== "cancelled";
  const upfrontStatus = order.upfrontPaymentStatus ?? "not_required";
  // Pre-PO Shipping tab (SO→PO Round 1) unlocks at "Client Approved" — a Sales
  // Order the client has signed off (soApprovedAt), an upfront SO that's been
  // paid, or one already past approval (in_production+). Before that the tab
  // explains-don't-hides. Mirrors commonsku's "Shipping unlocks at Client
  // Approved" gate.
  const soClientApproved = isSalesOrderView && isQuoteClientApproved(order);
  const unlockUntil = order.linesUnlockExpiresAt
    ? new Date(order.linesUnlockExpiresAt).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

  // Published link-only: the estimate link is live but no email was ever
  // sent. "Quote — Sent" would imply the client was emailed — say "Link
  // shared" instead. Only the ambiguous quote/sent badge is overridden;
  // later states (viewed/approved) reflect real client actions. Mirrors
  // PresentationDetailPage's showLinkOnlyBadge.
  const emailSent = data.email_sent ?? false;
  const showLinkOnlyBadge =
    order.statusStage === "quote" &&
    order.statusState === "sent" &&
    !emailSent;

  // Single-CTA grammar (UX S2, 2026-07-25). Exactly ONE primary action in every
  // quote state:
  //   draft              → Send primary ("Send to customer"), Convert outline
  //   sent / viewed /
  //   changes_requested  → Convert primary (Send hidden)
  //   approved           → Convert primary, Send outline ("Resend")
  // Convert is therefore primary in EVERY quote state EXCEPT draft. Code review
  // iter 1 caught the earlier `convertVariant = isApprovedEstimate ? primary :
  // outline`, which left sent/viewed/changes_requested with NO primary CTA.
  // Kept as derived booleans so the statusState reference stays OUT of the
  // button render blocks (the convert-button gate asserts no statusState gate
  // crept into its visibility conditional).
  const isDraftEstimate =
    order.statusStage === "quote" && order.statusState === "draft";
  const isApprovedEstimate =
    order.statusStage === "quote" && order.statusState === "approved";
  // Quote states (CEO 2026-10-15): draft → "Send to customer"; a change
  // request → "Send revised quote" (primary — it's the rep's next step, and
  // sending puts it back in front of the client to approve); sent / viewed /
  // approved → "Resend".
  const isChangesRequested =
    order.statusStage === "quote" && order.statusState === "changes_requested";
  const sendVariant = isDraftEstimate || isChangesRequested ? "primary" : "outline";
  const sendLabel = isDraftEstimate
    ? "Send to customer"
    : isChangesRequested
      ? "Send revised quote"
      : "Resend";
  const convertVariant = isDraftEstimate ? "outline" : "primary";
  // Offline approval (2026-07-26). A rep can record a verbal/offline client
  // approval as a SECONDARY affordance — never a primary CTA, never shown once
  // approved. Reachable on an estimate awaiting a decision (sent/viewed/
  // changes_requested, not draft, not yet approved) and on a non-upfront Sales
  // Order across the whole post-commit range (committed..invoiced, not closed)
  // the client hasn't signed off — an upfront SO collects via Approve & Pay.
  // The estimate branch keys off approvedAt; the SO branch off the DISTINCT
  // soApprovedAt (approvedAt is carried forward by convert_to_so and would
  // wrongly hide the affordance on the majority flow). Mirrors the server matrix
  // in POST /orders/:id/mark-approved.
  const canMarkApproved =
    (order.statusStage === "quote" &&
      (order.statusState === "draft" ||
        order.statusState === "sent" ||
        order.statusState === "viewed" ||
        order.statusState === "changes_requested") &&
      order.approvedAt == null) ||
    (isSoSignoffReachable(order.statusStage) &&
      order.requiresUpfrontPayment !== true &&
      order.soApprovedAt == null);
  // Rep-side approval record (ux review 2026-07-26) — a persistent line so the
  // rep has visible confirmation, not just a vanished button. SO stages read the
  // DISTINCT so_approved_* fields; the estimate stage reads approved_at as today.
  // A null name means it was recorded offline by staff (no client identity).
  const approvalStamp = isSalesOrderView
    ? { at: order.soApprovedAt, by: order.soApprovedByName }
    : { at: order.approvedAt, by: order.approvedByName };
  // Two-primary fix (ui review 2026-07-26). At shipped, the NextActionCta card
  // still renders (shouldShowNextAction includes shipped) and owns a primary
  // button whenever its derived action is send_pos / add_tracking. So
  // Convert-to-Invoice may be primary at shipped ONLY when the NextAction is
  // "complete" (the CTA then shows a quiet line, no button) — otherwise it stays
  // outline and the CTA owns the sole primary. Exactly one primary at shipped.
  const nextAction = deriveNextAction(
    { hasProductLines: lines.some((l) => l.serviceType === "product") },
    (shippedPosData?.rows ?? []).filter((r) => r.orderId === order.id),
  );
  const shippedNextActionKind = nextAction.kind;
  const convertInvoiceVariantAtShipped =
    shippedNextActionKind === "complete" ? "primary" : "outline";
  // Eyebrow noun — the document IS an Estimate (quote) or a Quote
  // (committed+). Never the generic "Order". Uses the SAME shared predicate as
  // the Preview button so the two can't disagree.
  const documentNoun = "Quote";

  // Create-POs preconditions (2026-07-26; per-location artwork gate T15) — the
  // client mirror of the server's PoPrereqError checks (derivePosFromOrder),
  // computed from data this page already holds so the "Create purchase orders"
  // button DISABLES with an honest reason ("Add a shipping address first" /
  // vendor missing / the exact (item, location) artwork pairs still missing a
  // logo file, name, or proof type) instead of a click → 409 → guess ping-pong.
  // Runs the IDENTICAL per-location decision the server runs, so the button and
  // the endpoint can't drift. The server stays the authoritative backstop
  // (missing_artwork keeps its override).
  const productLines = lines.filter((l) => l.serviceType === "product");
  // Product lines with no client price anywhere (line or any variant) — the
  // estimate's "unresolved pricing" note. Display only.
  const unpricedNames = productLines
    .filter(
      (l) =>
        !(Number(l.unitPrice) > 0) &&
        !l.variants.some((v) => v.unitPrice != null && Number(v.unitPrice) > 0),
    )
    .map((l) => l.productSnapshot?.name ?? l.description ?? "Item");
  const poPrecondition = derivePoPreconditions({
    hasShippingAddress: order.shippingAddressId !== null,
    blankOrder: order.blankOrder,
    productLines: productLines.map((l) => ({
      id: l.id,
      itemName: l.productSnapshot?.name ?? l.description ?? "Item",
      hasVendor: typeof l.productSnapshot?.vendorId === "string",
      needsArtwork: lineNeedsArtwork(l.decorationPath, order.blankOrder),
      missingSizes:
        l.variants.length === 0 &&
        (poVariantOptions ?? []).some((o) => o.lineId === l.id && o.needsBreakdown),
      // Per-location artwork gate (T15) — mirror the server's signals EXACTLY
      // (purchase-orders-builder.ts): a location is complete only with a logo
      // file + a logo name + a chosen proof type. logoColor is NOT gated.
      decorations: l.decorations
        // The "+ Add cost" holder is not an imprint location — no artwork.
        .filter((d) => d.kind !== "item_costs")
        .map((d) => ({
        location: d.location,
        hasLogoFile: d.logoFileId != null,
        hasLogoName: !!d.logoName?.trim(),
        hasProofType: d.proofType != null,
      })),
    })),
  });

  // Pre-create $0-cost gate (2026-07-26): a PO with a $0 product line is a
  // free-goods PO — it must NEVER mint silently. If any product line costs $0,
  // open the confirm instead of firing; otherwise create straight away. The
  // `skipArtwork` flag (the missing_artwork override) carries through the
  // confirm so "Create anyway" for artwork still works past the $0 gate. The
  // server backstops this exact rule (409 zero_cost) via the same shared helper.
  const startCreatePo = (skipArtwork: boolean) => {
    setCreatePoError(null);
    setPoArtworkOverride(skipArtwork);
    const zeroNames = zeroCostProductLineNames(
      productLines.map((l) => ({
        name: l.productSnapshot?.name ?? l.description ?? "Item",
        unitCost: l.netCost,
        isProduct: true,
      })),
    );
    if (zeroNames.length > 0) {
      setZeroCostNames(zeroNames);
      return;
    }
    createPoMutation.mutate({ skipArtwork });
  };

  return (
    <div className="mx-auto max-w-none">
      {/* Header — ONE compact band: eyebrow · #number · status/payment pills on
          the left, actions on the right. The old "← Estimates" back-link and
          the "for {client} · {project}" line are dropped: this page nests inside
          ProjectDetailLayout, whose breadcrumb band + left sub-nav already carry
          the client/project context, so both were pure duplication (CEO
          2026-07-25 density pass). Pattern: ProjectDetailPage.tsx:201-226. */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            {documentNoun}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
            #{order.orderNumber}
          </h1>
          <StatusPill
            tone={
              showLinkOnlyBadge
                ? "warn"
                : orderStatusTone(order.statusStage, order.statusState)
            }
          >
            {showLinkOnlyBadge
              ? "Link shared — no email sent"
              : orderStatusLabel(order.statusStage, order.statusState, {
                  omitStagePrefix: true,
                })}
          </StatusPill>
          {order.requiresUpfrontPayment && upfrontStatus !== "not_required" && (
            <span
              className={
                upfrontStatus === "paid"
                  ? "inline-flex items-center whitespace-nowrap rounded bg-ok-bg px-2 py-1 text-[11px] font-medium text-ok"
                  : "inline-flex items-center whitespace-nowrap rounded bg-warn-bg px-2 py-1 text-[11px] font-medium text-warn"
              }
            >
              {upfrontStatus === "paid"
                ? "Payment received"
                : "Payment not received"}
            </span>
          )}
          </div>
          {/* Facts row (handoff W10/W11/W12): who it's for, what it's worth,
              when it's needed. Document status stays in the pill above. */}
          <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
            {client && <HeaderFact label="Client" value={client.clientName} />}
            {/* No money here on purpose: the ONE client total is the right-hand
                totals card (handoff W12), so a second figure can never disagree. */}
            <HeaderFact label="Items" value={productLines.length.toLocaleString()} />
            <HeaderFact
              label="Units"
              value={productLines
                .reduce((sum, l) => sum + (l.quantityTotal > 0 ? l.quantityTotal : 0), 0)
                .toLocaleString()}
            />
            {approvalStamp.at && (
              <HeaderFact label="Approved" value={formatDate(approvalStamp.at)} />
            )}
          </dl>
        </div>
        {canEdit && !isEditing && (
          <div className="flex flex-wrap gap-2 xl:max-w-[62%] xl:justify-end">
            {/* Send to customer — opens the recipient picker (CEO 2026-07-20:
                a clear "how do I send this?" affordance + "copy his boss").
                Estimate (quote/draft): emails the binding-approval link and
                moves it draft → sent. Quote (committed/in_production):
                emails the Approve & Pay / approval link and records the send
                WITHOUT a state change. */}
            {((order.statusStage === "quote" && order.statusState !== "cancelled") ||
              order.statusStage === "committed" ||
              order.statusStage === "in_production") &&
              client && (
                <Button
                  type="button"
                  variant={sendVariant}
                  size="sm"
                  disabled={sendApprovalMutation.isPending}
                  onClick={() => {
                    setSendApprovalMsg(null);
                    setNoContactPrompt(false);
                    setShowSendModal(true);
                  }}
                  title={
                    order.statusStage === "quote"
                      ? "Email this quote to the client (and anyone you copy). They review it and approve on the link."
                      : "Email this quote to the client (and anyone you copy) with the link to approve — and pay, if an upfront payment is required."
                  }
                >
                  {sendApprovalMutation.isPending ? "Sending…" : sendLabel}
                </Button>
              )}
            {/* Convert to Quote — any quote-stage estimate (operator
                2026-06-11: no approval gate; the client "Approve Order" binding
                click is the formal path but isn't required to convert). ONE Sales
                Order per project (CEO 2026-07-20): when the project already has
                one we HIDE this button (2026-07-26 — a disabled dead control was
                the CEO "no-value button" complaint). Explain-don't-hide is
                satisfied by the info line below, which names the SO + links to
                Clone; the dead button is not the explanation. */}
            {/* CEO 2026-10-15: no "Convert to Quote" step — Estimate and Sales
                Order are one Quote, and approval (client or Mark approved) puts
                the order live by itself. "Start order" remains ONLY for a quote
                that is approved but not live yet (e.g. approved before this
                change, or an upfront quote the rep starts by hand). */}
            {order.statusStage === "quote" && order.statusState === "approved" && (
                <Button
                  type="button"
                  variant={convertVariant}
                  size="sm"
                  disabled={convertMutation.isPending}
                  onClick={() => {
                    setConvertError(null);
                    setShowConvertModal(true);
                  }}
                  title="The client approved — start the order (purchase orders and shipping unlock)."
                >
                  {convertMutation.isPending
                    ? "Starting…"
                    : "Start order"}
                </Button>
              )}
            {/* Convert to Invoice — PROMINENT at shipped only (2026-07-26): once
                the order has shipped, invoicing IS the next action, so it stays a
                primary CTA. At committed/in_production the NextActionCta below
                owns the primary next step (create/send POs), so Convert to
                Invoice is DEMOTED to a secondary control in the trailing cluster
                (see below) — one clear primary next action per stage, which was
                the CEO "which button do I press?" complaint. Backend still
                enforces one-final-invoice-per-order via the unique index. */}
            {order.statusStage === "shipped" && (
              <Button
                type="button"
                variant={convertInvoiceVariantAtShipped}
                size="sm"
                disabled={convertToInvoiceMutation.isPending}
                onClick={() => {
                  setConvertError(null);
                  convertToInvoiceMutation.mutate();
                }}
                title="Create a final invoice from this Quote — subtotal sums the line items, tax starts at 0 (adjust on the invoice page if applicable)."
              >
                {convertToInvoiceMutation.isPending
                  ? "Creating…"
                  : "Convert to Invoice"}
              </Button>
            )}
            {/* Create PO moved OUT of this action row (CEO 2026-07-24). The
                sales→production handoff is now the single prominent NextActionCta
                below (state-derived): a small "Create PO" button buried among
                Convert/Clone/Edit was exactly the "I don't know what to do next"
                UX the CEO flagged. The CTA reuses the SAME createPoMutation +
                artwork-override confirm flow. */}
            {/* Upfront payment (2026-07-20) replaces the old deposit-invoice
                button — the terms + status live in the UpfrontPayment
                panel below, and the client pays on the /o/:uuid share page.
                "View payment" (the deposit-type invoice) is offered there once
                a payment has been initiated. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(
                    `${window.location.origin}/o/${order.publicShareUuid}`,
                  )
                  .then(
                    () => setCopiedAt(Date.now()),
                    () =>
                      setSendApprovalMsg(
                        "Could not copy — your browser blocked clipboard access.",
                      ),
                  );
              }}
              title={
                shareLinkCopied
                  ? "Link copied to clipboard"
                  : "Copy the client share link"
              }
            >
              {shareLinkCopied ? (
                <>
                  <Check className="h-3.5 w-3.5" /> Copied!
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" /> Copy link
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(
                  `/o/${order.publicShareUuid}`,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              title={
                isSalesOrderView
                  ? "Open the client view in a new tab — shows the Quote as the client will see it. Use the Download as PDF button there."
                  : "Open the client view in a new tab — shows the quote as the client will see it. Use the Download as PDF button there."
              }
            >
              <ExternalLink className="h-3.5 w-3.5" /> {previewDocLabel}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setCloneError(null);
                setShowCloneModal(true);
              }}
              title="Clone this order into a new project. Pick a target client + name; lines + decorations + charges carry. PO/Bill/Invoice/Commission rows do NOT."
            >
              <CopyPlus className="h-3.5 w-3.5" /> Clone
            </Button>
            {/* What the client got each time it was sent (Stephanie 2026-10-27). */}
            <Button type="button" variant="outline" size="sm" onClick={() => setShowSentCopies(true)}>
              <History className="h-3.5 w-3.5" /> Sent copies
            </Button>
            {/* Mark approved (2026-07-26) — record an offline/verbal client
                approval. SECONDARY (ghost) affordance, never a primary CTA and
                never shown once approved. Applies the same client_approved
                sign-off with actor=staff. Does NOT gate conversion (#208). */}
            {canMarkApproved && (
              <Button
                id="so-approval"
                type="button"
                variant="ghost"
                size="sm"
                disabled={markApprovedMutation.isPending}
                onClick={() => {
                  setMarkApprovedError(null);
                  markApprovedMutation.mutate();
                }}
                title="Record that the client approved offline (by phone/email). Stamps the sign-off — it does not collect any required payment."
              >
                {markApprovedMutation.isPending ? "Recording…" : "Mark approved"}
              </Button>
            )}
            {/* Convert to invoice straight from a quote that isn't approved yet —
                the client approved another way, e.g. sent their own PO
                (Stephanie 2026-11-02). Asks first; nothing is emailed. */}
            {order.statusStage === "quote" &&
              order.statusState !== "approved" &&
              order.requiresUpfrontPayment !== true &&
              (confirmInvoiceNow ? (
                <span className="inline-flex flex-wrap items-center gap-2 text-sm text-ink">
                  Mark approved and create the invoice? The quote won't be sent.
                  <Button
                    type="button"
                    size="sm"
                    disabled={invoiceNowMutation.isPending || convertToInvoiceMutation.isPending}
                    onClick={() => invoiceNowMutation.mutate()}
                  >
                    {invoiceNowMutation.isPending || convertToInvoiceMutation.isPending ? "Creating…" : "Yes, create invoice"}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setConfirmInvoiceNow(false)}>
                    No
                  </Button>
                </span>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setConvertError(null);
                    setConfirmInvoiceNow(true);
                  }}
                  title="The client approved without the quote (e.g. sent their PO): records the approval, starts the order and creates the invoice. Nothing is emailed."
                >
                  Convert to invoice
                </Button>
              ))}
            {/* Convert to Invoice — DEMOTED (ghost) at committed/in_production so
                the NextActionCta owns the primary next action. It stays PROMINENT
                at shipped (rendered in the primary area above). */}
            {(order.statusStage === "committed" ||
              order.statusStage === "in_production") && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={convertToInvoiceMutation.isPending}
                onClick={() => {
                  setConvertError(null);
                  convertToInvoiceMutation.mutate();
                }}
                title="Create a final invoice from this Quote — subtotal sums the line items, tax starts at 0 (adjust on the invoice page if applicable)."
              >
                {convertToInvoiceMutation.isPending
                  ? "Creating…"
                  : "Convert to Invoice"}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={enterEdit}>
              Edit
            </Button>
            {/* New estimate (P2) — the in-context escape hatch that replaces the
                removed Actions ▾ dropdown. Estimate view only, gated to the
                stages the rule engine offers create_estimate. Routes to the
                Estimate tab's create form via ?new=1 (the tab's ONE create path,
                no duplicated mutation) — which also bypasses the tab's
                single-estimate auto-redirect so the rep isn't bounced back here.
                Rendered LAST and as ghost (ui review): starting a second estimate
                is a low-frequency side path — it sits after Edit in the same
                tertiary tier, never leading the stage's primary Send/Convert CTA. */}
            {isEstimate &&
              projectCtx != null &&
              canCreateEstimateAtStage(projectCtx.project.statusStage) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    navigate(`/projects/${order.projectId}/quotes?new=1`)
                  }
                  title="Start another quote on this opportunity."
                >
                  <Plus className="h-3.5 w-3.5" /> New quote
                </Button>
              )}
            {/* Delete a quote — a duplicate or a copy made by mistake
                (Stephanie 2026-10-30). The server refuses while it has a live
                invoice or PO and says which. */}
            <DeleteEntityAction
              deletePath={`/orders/${order.id}`}
              triggerLabel="Delete quote"
              allowedRoles={["admin", "client_rep", "order_rep"]}
              confirmTitle="Delete this quote?"
              confirmBody={
                <>
                  This removes <strong className="font-semibold text-brown-500">Quote #{order.orderNumber}</strong> from the
                  project. Its link to the client stops working. A quote with an invoice or PO can't be deleted until
                  those are voided or cancelled.
                </>
              }
              redirectTo={`/projects/${order.projectId}/quotes`}
              flashMessage={`Quote #${order.orderNumber} deleted.`}
              errorFallback="We couldn't delete the quote. Nothing was changed — try again."
              invalidateKeys={[["project", order.projectId], ["merchd.projectDetail"]]}
            />
          </div>
        )}
      </div>
      {cloneError && (
        <p className="mb-4 text-sm text-rust-600">{cloneError}</p>
      )}
      {id && (
        <LogoColourChecks
          orderId={id}
          onResolved={() => {
            setConvertError(null);
            void queryClient.invalidateQueries({ queryKey: detailQueryKey });
          }}
        />
      )}
      {convertError && (
        <p className="mb-4 text-sm text-rust-600">{convertError}</p>
      )}
      {/* Where the quote stands with the client (CEO 2026-10-15): the client's
          change note, or that it's waiting on them — with the next step. */}
      {order.clientChangeRequest ? (
        <div className="mb-4 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-[15px] text-ink" role="status">
          <p className="font-semibold">
            {order.clientChangeRequest.by ?? "The client"} asked for a change · {formatDate(order.clientChangeRequest.at)}
          </p>
          <p className="mt-1 whitespace-pre-line">
            {order.clientChangeRequest.note?.trim() || "No note — reach out to ask what they'd like changed."}
          </p>
          {order.statusStage === "quote" && (
            <p className="mt-1 text-sm text-muted">Edit the quote, then use “Send revised quote” so they can approve the new version.</p>
          )}
        </div>
      ) : order.statusStage === "quote" && (order.statusState === "sent" || order.statusState === "viewed") ? (
        <p className="mb-4 rounded-lg border border-rule bg-white px-4 py-3 text-[15px] text-ink" role="status">
          {order.statusState === "viewed" ? "The client has opened this quote" : "Sent — waiting on the client"}. When they approve, the order goes live by itself.
        </p>
      ) : null}
      {markApprovedError && (
        <p className="mb-4 text-sm text-rust-600">{markApprovedError}</p>
      )}
      {/* Persistent approval record (ux review 2026-07-26) — the rep needs a
          visible confirmation after Mark approved / a client SO sign-off, not
          just a vanished button. SO stages read the DISTINCT so_approved_* fields;
          the estimate stage reads approved_at. A null name = recorded offline by
          staff (no client identity captured). */}
      {approvalStamp.at && (
        <p className="mb-4 flex items-center gap-1.5 text-xs text-brown-400">
          <Check className="h-3.5 w-3.5 shrink-0 text-muted" />
          <span>
            {approvalStamp.by
              ? `Approved by ${approvalStamp.by}`
              : "Approved offline — recorded by staff"}{" "}
            on {formatDate(approvalStamp.at)}
          </span>
        </p>
      )}
      {commissionWarnings.length > 0 && (
        /* ⚠️ NEUTRAL container, rust reserved for the headline. The invoice
           SUCCEEDED and needs follow-up; a full rust box is what this file uses
           for a genuine failure (`createPoError` below), and a rep's first read
           is the colour, not the words. Matches the `orphanedPos` "flagged for
           review" panel, which is the house pattern for exactly this case. */
        <div className="mb-4 rounded-lg border border-brown-100 bg-cream-50 px-4 py-3">
          <p className="text-sm font-semibold text-rust-600">
            Invoice created — but{" "}
            {commissionWarnings.length === 1
              ? "one person"
              : `${commissionWarnings.length} people`}{" "}
            earned no commission on it
          </p>
          <ul className="mt-2 space-y-1 text-xs text-brown-500">
            {commissionWarnings.map((w) => (
              <li key={`${w.repId}:${w.scenario}`}>{w.message}</li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-brown-400">
            The invoice is fine and has been created. Commission is worked out
            at that moment, so this needs a finance or admin user to set the
            rate and re-run commissions for this invoice.
          </p>
          <button
            type="button"
            onClick={() => {
              const pid = data?.order.projectId;
              navigate(pid ? `/projects/${pid}/invoice` : "/projects");
            }}
            className="mt-3 inline-flex items-center gap-1 rounded-md border border-peach-400 bg-peach-200 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brown-600 hover:bg-peach-300"
          >
            Continue to the invoice
          </button>
        </div>
      )}
      {(createdPos.length > 0 || orphanedPos.length > 0) && (
        <div className="mb-4 rounded-lg border border-brown-100 bg-cream-50 px-4 py-3 text-sm text-brown-500">
          {createdPos.length > 0 && (
            <>
              <p className="font-semibold">
                {createdPos.length} purchase order
                {createdPos.length === 1 ? "" : "s"} created
              </p>
              <ul className="mt-2 space-y-1">
                {createdPos.map((p) => {
                  const pid = data?.order.projectId;
                  const label =
                    p.poType === "decoration_service"
                      ? "decoration"
                      : "supplier";
                  return (
                    <li key={p.id}>
                      <Link
                        to={
                          pid ? `/projects/${pid}/pos/${p.id}` : `/pos/${p.id}`
                        }
                        className="font-semibold text-ink hover:text-muted"
                      >
                        PO #{p.poNumber} — {label} →
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {/* G3 — POs orphaned by this re-derive (a superseded decorator's PO),
              flagged for review, told to the rep now — not left to notice later. */}
          {orphanedPos.length > 0 && (
            <div className={createdPos.length > 0 ? "mt-3" : ""}>
              <p className="font-semibold text-rust-600">
                {orphanedPos.length} purchase order
                {orphanedPos.length === 1 ? "" : "s"} flagged for review
              </p>
              <p className="mt-0.5 text-brown-400">
                Superseded by a re-staged decorator — review and cancel if no
                longer needed.
              </p>
              <ul className="mt-1 space-y-1">
                {orphanedPos.map((p) => {
                  const pid = data?.order.projectId;
                  return (
                    <li key={p.id}>
                      <Link
                        to={
                          pid ? `/projects/${pid}/pos/${p.id}` : `/pos/${p.id}`
                        }
                        className="font-semibold text-ink hover:text-muted"
                      >
                        PO #{p.poNumber} — flagged →
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
      {createPoError && (
        <div className="mb-4 rounded-lg border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-600">
          <p>{createPoError}</p>
          {poArtworkOverride && (
            <button
              type="button"
              onClick={() => startCreatePo(true)}
              disabled={createPoMutation.isPending}
              className="mt-2 rounded-lg bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink-700 disabled:opacity-50"
            >
              {createPoMutation.isPending ? "Creating…" : "Create PO anyway"}
            </button>
          )}
        </div>
      )}
      {/* $0-cost free-goods confirm (2026-07-26). A PO with a $0 product line
          would tell the supplier the items are free — never mint it silently.
          The rep must explicitly confirm (skip_zero_cost_check); this mirrors
          the server's 409 zero_cost backstop. */}
      {zeroCostNames && (
        <div className="mb-4 rounded-lg border border-peach-300 bg-peach-100 px-4 py-3 text-sm text-brown-500">
          <p className="font-semibold">$0-cost items on this purchase order</p>
          <p className="mt-1 text-brown-400">
            {zeroCostNames.length > 0
              ? `${zeroCostNames.slice(0, 3).join(", ")}${
                  zeroCostNames.length > 3
                    ? ` and ${zeroCostNames.length - 3} more`
                    : ""
                } ${
                  zeroCostNames.length === 1 ? "has" : "have"
                } no cost — the supplier would read free goods.`
              : "One or more items have no cost — the supplier would read free goods."}{" "}
            Fix the costs on the Quote, or create the purchase order anyway.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={createPoMutation.isPending}
              onClick={() => {
                setZeroCostNames(null);
                createPoMutation.mutate({
                  skipArtwork: poArtworkOverride,
                  skipZeroCost: true,
                });
              }}
              title="Create the purchase order with $0-cost items (free goods)."
            >
              {createPoMutation.isPending ? "Creating…" : "Create PO anyway"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setZeroCostNames(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {sendApprovalMsg && (
        <p className="mb-4 rounded-lg border border-brown-100 bg-cream-50 px-4 py-3 text-sm text-brown-500">
          {sendApprovalMsg}
        </p>
      )}
      {noContactPrompt && (
        <div className="mb-4 rounded-lg border border-peach-300 bg-peach-100 px-4 py-3 text-sm text-brown-500">
          <p className="font-semibold">No contact to email</p>
          <p className="mt-1 text-brown-400">
            This client has no contact with an email address. Add a contact to
            email the estimate for approval — or share the link without
            emailing.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={!client}
              onClick={() => {
                if (client) navigate(`/clients/${client.clientId}/contacts`);
              }}
              title="Open this client's contacts to add an email address"
            >
              Add contact
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={sendApprovalMutation.isPending}
              onClick={() =>
                sendApprovalMutation.mutate({ publishLinkOnly: true })
              }
              title="Publish the approval link without emailing anyone. The status message will say 'Link shared — no email sent'."
            >
              Share link only
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setNoContactPrompt(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Post-approval next-action CTA (CEO 2026-07-24). Renders on committed /
          in_production / shipped Quotes (never on estimates or cancelled
          orders — shouldShowNextAction gates the stage). ONE prominent, state-
          aware action so the next step is obvious: Create purchase orders →
          Review & send PO → Add tracking → complete. Reuses createPoMutation so
          the artwork-override confirm flow below still fires wherever the
          mutation is triggered. */}
      {/* Pre-PO Shipping plan (SO→PO Round 1). Placed ABOVE the Create-POs CTA
          so the page reads top-to-bottom as approve → stage shipping → create
          POs (the same configure-then-act order PoShippingSection documents).
          A Quote surface only; unlocks at Client Approved, explains-don't-
          hides before that. Rendered as a section (the order page is a stacked-
          section layout, not tabbed). */}
      {/* The item-by-item Shipping plan moved to the project's own Shipping
          tab (Stephanie 2026-10-12: "this should not be here … in commonsku
          there was a shipping tab"). A pointer stays here. */}
      {isSalesOrderView && (
        <p className="mb-4 text-sm text-brown-400">
          Plan where each item ships on the{" "}
          <Link
            to={`/projects/${order.projectId}/shipping`}
            className="font-semibold text-ink hover:text-muted"
          >
            Shipping tab →
          </Link>
        </p>
      )}

      {/* ── Commission panel (Finance/admin, compensation plans on, committed+) — CEO 2026-10-15.
          Self-gating component; keep this the only commission block on this page. ── */}
      <OrderCommissionPanel orderId={order.id} stage={order.statusStage} />

      {shouldShowNextAction(order.statusStage) && (
        <div id="create-pos">
          <NextActionCta
            orderId={order.id}
            projectId={order.projectId}
            hasProductLines={lines.some((l) => l.serviceType === "product")}
            canCreatePo={canCreatePo}
            onCreatePo={() => startCreatePo(false)}
            onCreateAnyway={() => startCreatePo(true)}
            precondition={poPrecondition}
            createPoPending={createPoMutation.isPending}
            shipsTo={shortShipTo(order.shippingAddressSnapshot)}
            // Purchase orders are created on the Shipping tab once every item's
            // shipping is filled in (Stephanie 2026-10-13).
            createOnShippingHref={`/projects/${order.projectId}/shipping`}
          />
        </div>
      )}

      {/* E3 — scoped re-create. A cancelled leg (supplier or decoration) with no
          live equivalent gets a per-vendor "Create PO for {vendor}" button that
          re-derives JUST that vendor group (only_vendor_id) — surfaced even with
          live siblings, the only UI that reaches the scoped-derive param. */}
      {shouldShowNextAction(order.statusStage) &&
        // F3 — hide when the primary CTA is already 'create_pos' (all-cancelled /
        // none-created): the NextActionCta owns creation there, so this panel
        // would be a redundant duplicate. Role-gate on canCreatePo so a
        // non-creator never sees a button that 403s.
        nextAction.kind !== "create_pos" &&
        canCreatePo &&
        nextAction.recreatableVendors.length > 0 && (
          <div className="mb-4 rounded-lg border border-peach-300 bg-peach-100 px-4 py-3 text-sm text-brown-500">
            <p className="font-semibold">
              {nextAction.recreatableVendors.length === 1
                ? "A cancelled purchase order can be re-created"
                : "Cancelled purchase orders can be re-created"}
            </p>
            <p className="mt-1 text-brown-400">
              Re-create the missing leg for a vendor — only that vendor's PO(s)
              are rebuilt; live POs are left alone.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {nextAction.recreatableVendors.map((v) => (
                <Button
                  key={v.vendorId}
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={createPoMutation.isPending}
                  onClick={() =>
                    createPoMutation.mutate({ onlyVendorId: v.vendorId })
                  }
                >
                  Create PO for {v.vendorName}
                </Button>
              ))}
            </div>
          </div>
        )}

      {/* CEO 2026-07-24: the document read "pushed to the right" because header
          + body were a side-by-side md:grid-cols-3 split — the header sat in the
          left 1/3 and ALL the document content (lines, totals, addresses, terms)
          was crammed into the right 2/3, leaving a permanent empty left gutter.
          PresentationDetailPage.tsx:277 stacks everything full-width vertically
          (mx-auto max-w-none space-y-6). Match that editorial left-aligned stack:
          header full-width on top, document body full-width below. */}
      <div className="space-y-8">
        {/* Header fields (full width, top of the document stack) */}
        <div className="space-y-6">
          {!isEditing ? (
            <ReadOnlyHeader
              order={order}
              showCustomerPo={order.statusStage !== "quote"}
              canEdit={canEdit}
              onAddIntroduction={enterEdit}
              onNotesSaved={() => void queryClient.invalidateQueries({ queryKey: detailQueryKey })}
            />
          ) : (
            form !== null && (
              <EditableHeader
                form={form}
                showCustomerPo={order.statusStage !== "quote"}
                currency={order.currency ?? "USD"}
                onChange={setForm}
                onCancel={cancelEdit}
                onSave={() => {
                  const validation = validate(form);
                  if (validation !== null) {
                    setSaveError(validation);
                    return;
                  }
                  const body = buildPatchBody(form, order);
                  if (Object.keys(body).length === 0) {
                    cancelEdit();
                    return;
                  }
                  saveMutation.mutate(body);
                }}
                saving={saveMutation.isPending}
                saveError={saveError}
              />
            )
          )}
          {/* Rush / Firm in-hands date — internal, marked by the sales rep (Stephanie 2026-10-12). */}
          <OrderUrgencyToggles orderId={order.id} canEdit={canEdit} />
        </div>

        {/* Document body: lines + totals + addresses + terms (full width) */}
        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0">
          {/* Addresses (PR #153) — billing + shipping snapshots from
              the order. Shown side-by-side. Operator wants the
              estimate view to "look like an estimate" — addresses
              are part of that proposal-grade rendering. At the top, above
              the line items (Stephanie 2026-10-14). */}
          <div className="mb-6">
          <AddressesPanel
            billing={order.billingAddressDisplay ?? order.billingAddressSnapshot}
            shipping={order.shippingAddressDisplay ?? order.shippingAddressSnapshot}
            clientId={client?.clientId ?? null}
            billingAddressId={order.billingAddressId}
            shippingAddressId={order.shippingAddressId}
            canEdit={canEdit}
            applying={saveMutation.isPending}
            onApply={(kind, addressId) =>
              saveMutation.mutate(
                kind === "billing"
                  ? { billing_address_id: addressId }
                  : { shipping_address_id: addressId },
              )
            }
          />
          </div>
          {/* Locked to the top while you scroll, under the page header — the
              floating button is gone (Stephanie 2026-10-27: "make it look
              like a locked top row"). */}
          <div className="sticky top-[68px] z-10 -mx-2 border-b border-rule bg-canvas px-2 py-2 mb-3 flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
              Line items
            </h2>
            {linesEditable && (
              <div className="flex items-center gap-2">
                {/* Native <select> styled as a button. Pick a type →
                    creates a line of that type. Stays at the placeholder
                    so the rep can add multiple in a row without resetting
                    state. The Shipping option populates the /o/:uuid
                    Shipping totals row automatically. */}
                <select
                  value=""
                  onChange={(e) => {
                    const t = e.target.value as ServiceLineType | "";
                    if (t === "") return;
                    addServiceMutation.mutate(t);
                    e.currentTarget.value = "";
                  }}
                  disabled={addServiceMutation.isPending}
                  className="h-9 cursor-pointer rounded-md border border-brown-100 bg-white px-3 text-sm font-semibold text-brown-500 hover:border-peach-400 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200 disabled:opacity-50"
                  aria-label="Add a service line"
                >
                  <option value="">
                    {addServiceMutation.isPending ? "Adding…" : "+ Add service"}
                  </option>
                  {SERVICE_LINE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {SERVICE_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
                <Button type="button" onClick={() => setShowAddProduct(true)}>
                  <Plus className="mr-1 h-4 w-4" /> Add product
                </Button>
              </div>
            )}
          </div>

          {/* Line-edit lock notice + admin escape hatch (snag #8).
              Gated on the SAME value the table is (linesEditable, server-
              derived), not on the retired stage rule. Review caught this
              keyed to `!stageLinesEditable` while the table below already
              used the new rule — so an in-production order with no invoice
              was genuinely editable while this banner insisted it was locked
              and offered a pointless unlock button. Exactly the client/server
              drift this change set out to remove, left in the file doing the
              removing. If you touch one, touch the other. */}
          {!linesEditable && (
            <div className="mb-4">
              {linesUnlocked ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-peach-300 bg-peach-100 px-4 py-3">
                  <p className="flex items-center gap-2 text-sm font-medium text-brown-500">
                    <LockOpen className="h-4 w-4 shrink-0 text-muted" />
                    <span>
                      Line editing unlocked until{" "}
                      <span className="font-semibold">{unlockUntil}</span>
                      {order.linesUnlockedByName
                        ? ` by ${order.linesUnlockedByName}`
                        : ""}{" "}
                      — edits are audited
                    </span>
                  </p>
                  {isAdmin && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => relockLinesMutation.mutate()}
                      disabled={relockLinesMutation.isPending}
                    >
                      <Lock className="mr-1 h-4 w-4" />
                      {relockLinesMutation.isPending
                        ? "Re-locking…"
                        : "Re-lock now"}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brown-100 bg-cream-100 px-4 py-3">
                  <p className="flex items-center gap-2 text-sm text-brown-400">
                    <Lock className="h-4 w-4 shrink-0 text-brown-300" />
                    Line items are locked — this order has been invoiced.
                  </p>
                  {isAdmin && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => unlockLinesMutation.mutate()}
                      disabled={unlockLinesMutation.isPending}
                    >
                      <LockOpen className="mr-1 h-4 w-4" />
                      {unlockLinesMutation.isPending
                        ? "Unlocking…"
                        : "Unlock editing (30 min)"}
                    </Button>
                  )}
                </div>
              )}
              {unlockMsg && (
                <p className="mt-2 text-xs font-medium text-rust-500">
                  {unlockMsg}
                </p>
              )}
            </div>
          )}

          {/* Soft double-entry warning (packages/shared/duplicate-fee.ts).
              Rep editor surface only — never rendered on PublicOrderPage. A
              rush/setup/other fee can live as a service line AND as a decoration
              charge; both feed totals + booked margin, so flag the overlap.
              Informational only: no disabled controls, no gated mutation.
              Palette + structure mirror the "line editing unlocked" peach
              caution banner above; AlertTriangle (not Info) marks it a caution. */}
          {duplicateFee.warns && (
            <p className="mb-4 flex items-center gap-2 rounded-lg border border-peach-300 bg-peach-100 px-4 py-3 text-sm font-medium text-brown-500">
              <AlertTriangle className="h-4 w-4 shrink-0 text-rust-400" />
              <span>
                This order has a rush/setup/other fee entered as a line item AND
                as a decoration charge — double-check it isn't entered twice.
              </span>
            </p>
          )}

          {/* What the client's quote page shows (CEO 2026-10-15). These are
              Flow-only controls — the client page shows their RESULT, never
              the switches. Hidden prices are removed from the client's data
              and PDF, not just hidden. */}
          <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-rule bg-white px-4 py-3 text-sm text-ink">
            <span className="font-semibold">Client sees</span>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!order.hidePricing}
                disabled={saveMutation.isPending}
                onChange={(e) => saveMutation.mutate({ hide_pricing: !e.target.checked })}
                className="h-4 w-4 accent-ink"
              />
              Prices and totals
            </label>
            {order.showApproval != null && (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={order.showApproval}
                  disabled={showApprovalMutation.isPending}
                  onChange={(e) => showApprovalMutation.mutate(e.target.checked)}
                  className="h-4 w-4 accent-ink"
                />
                Approval section
              </label>
            )}
          </div>

          {/* Blank order (Stephanie 2026-10-14): unticked = every item needs
              its logo before the Shipping tab and Create POs. Shown once the
              DB column exists (blankOrder not null). */}
          {order.blankOrder != null && (
            <label className="mb-3 flex items-start gap-2.5 rounded-lg border border-rule bg-white px-4 py-3 text-sm text-ink">
              <input
                type="checkbox"
                checked={order.blankOrder}
                disabled={blankOrderMutation.isPending}
                onChange={(e) => blankOrderMutation.mutate(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-ink"
              />
              <span>
                <span className="font-semibold">Blank order</span>
                <span className="block text-xs text-muted">
                  {order.blankOrder
                    ? "No logo needed — shipping and POs can go ahead without artwork."
                    : "Leave unticked when there's a logo: every item needs its logo before Shipping and Create POs."}
                </span>
              </span>
            </label>
          )}

          {/* Charge Qty boxes are typeable once Jake's step has run. */}
          <ChargeQtyReadyContext.Provider value={order.chargeQuantityReady === true}>
          <LinesTable
            lines={lines}
            sections={data?.sections ?? []}
            totalsTarget={totalsEl}
            currency={order.currency ?? "USD"}
            editable={linesEditable}
            cardFee={
              order.requiresUpfrontPayment === true && order.ccFeePaidBy === "client" && (order.ccFeeCents ?? 0) > 0
                ? { cents: order.ccFeeCents ?? 0, pct: order.ccSurchargePct ?? null }
                : null
            }
            orderId={id}
            projectId={order.projectId}
            detailQueryKey={detailQueryKey}
            taxCode={order.taxCode ?? null}
            taxRatePct={order.defaultTaxRate}
            itemCostsReady={order.itemCostsReady === true}
            shippingNotes={
              order.shippingNotes != null
                ? {
                    notes: order.shippingNotes,
                    canEdit,
                    onSaved: () => void queryClient.invalidateQueries({ queryKey: detailQueryKey }),
                  }
                : null
            }
            hidePricing={order.hidePricing}
            onToggleHidePricing={
              isEstimate
                ? (next) => saveMutation.mutate({ hide_pricing: next })
                : undefined
            }
            onAddProductToSection={(sectionId) => {
              setAddTargetSectionId(sectionId);
              setShowAddProduct(true);
            }}
          />
          </ChargeQtyReadyContext.Provider>
          {lineNote && (
            <p className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-peach-300 bg-peach-100 px-4 py-2 text-sm font-medium text-brown-500">
              <span>{lineNote}</span>
              <button
                type="button"
                onClick={() => setLineNote(null)}
                className="shrink-0 text-xs font-semibold text-brown-400 hover:text-rust-500"
              >
                Dismiss
              </button>
            </p>
          )}
          {showShippingEstimate && linesEditable && !isEstimate && (
            <ShippingEstimateModal
              orderId={id}
              shipToZip={order.shippingAddressSnapshot?.postalCode ?? null}
              existingLineId={lines.find((l) => l.serviceType === "shipping" && (l.description ?? "").startsWith(ESTIMATE_LINE_PREFIX))?.id ?? null}
              onClose={() => setShowShippingEstimate(false)}
              onApplied={() => {
                setShowShippingEstimate(false);
                void queryClient.invalidateQueries({ queryKey: detailQueryKey });
              }}
            />
          )}
          {showAddProduct && linesEditable && (
            <AddProductModal
              currency={order.currency ?? "USD"}
              showQtyPerAdd
              onAdd={addRow}
              // Not in the catalogue yet? Add it from here (Stephanie
              // 2026-10-14) — same form as the presentation's.
              customProductSlot={
                <OrderCustomProduct
                  onCreated={addCustomLine}
                  onItemAdded={() => void queryClient.invalidateQueries({ queryKey: detailQueryKey })}
                  addedText={addTargetSectionId !== null ? "Added under this category." : `Added to the ${isEstimate ? "estimate" : "quote"}.`}
                  addLabel="Add to quote"
                />
              }
              onClose={() => {
                setShowAddProduct(false);
                // Clear the target, or the NEXT plain "Add product" would
                // silently file into the last category the rep used.
                setAddTargetSectionId(null);
              }}
            />
          )}

          {/* Totals — the ONLY totals render is DocumentTotals inside the lines
              card above (it now renders under BOTH the document and table
              views, with the estimate-only Hide-totals control). The legacy
              standalone totals panel that used to live here was a second,
              duplicate block (CEO 2026-07-25 density pass) and has been removed. */}

          {/* Estimate defaults (PR #154b cont., 2026-06-01) — per-order
              default tax rate (drives the Tax totals row) + default
              margin (auto-prices new product lines from net_cost). Both
              opt-in; null = today's behavior.
              CEO 2026-07-24: this panel is ESTIMATE-only. linesEditable is also
              true on a committed Quote (and via the admin unlock window),
              so it must be gated on statusStage === "quote" to keep it off the
              SO — the CEO didn't recognize "Estimate defaults" on a Quote. */}
          {/* Estimate defaults + Terms & Conditions (CEO 2026-07-25 density
              pass). EstimateDefaultsPanel is ESTIMATE-only (quote + editable);
              TermsAndConditionsPanel renders for every canEdit role. The 2-col
              grid renders ONLY when BOTH panels are present (a quote) — UI review
              iter 1: an always-2-track grid reserved a permanent blank right
              gutter beside Terms on EVERY Quote. When defaults are hidden,
              Terms renders alone, full-width. Per-order Terms override (PR #155):
              empty textarea = no override; PublicOrderPage falls back to the
              hardcoded default copy. */}
          {/* Terms render full-width for every canEdit role; the estimate-only
              defaults moved to the right column (handoff W11), so there is no
              two-track grid to leave a blank gutter on a Quote. */}
          {canEdit && (
            <TermsAndConditionsPanel
              value={order.termsAndConditions}
              saving={saveMutation.isPending}
              onSave={(next) =>
                saveMutation.mutate({ terms_and_conditions: next })
              }
            />
          )}
        </div>
        {/* Right column (handoff W12): the ONE Client total (moved here from
            under the lines), then shipping. Sticks while the lines scroll. */}
        <aside aria-label="Order totals and shipping" className="space-y-6">
          {/* Upfront payment sits at the top of the right column, and the
              Client total under Shipping (Stephanie 2026-10-26). */}
          {/* Upfront payment (2026-07-20) — commonsku-style. Replaces the old
              deposit-invoice flow: the rep sets the terms here, the
              client approves + pays on /o/:uuid. Visible until the order is
              invoiced/closed/cancelled. */}
          {upfrontPanelVisible && (
            <UpfrontPaymentPanel
              order={order}
              currency={order.currency ?? "USD"}
              canEdit={canEdit}
              orderSubtotal={data.totals?.subtotal ?? null}
              saving={saveMutation.isPending}
              onSave={(patch) => saveMutation.mutate(patch)}
              onViewPayment={
                order.depositInvoiceId
                  ? () =>
                      navigate(
                        `/projects/${order.projectId}/invoice/${order.depositInvoiceId}`,
                      )
                  : undefined
              }
            />
          )}
          {/* Items billed on another quote's invoice (Stephanie 2026-10-16):
              the total above still lists them (it's the quote the client
              approved); invoicing THIS quote leaves them out. */}
          {lines.some((l) => l.billedOnInvoiceNumber) && (
            <section className="rounded-md border border-ok/30 bg-ok-bg px-[22px] py-4 text-sm text-ok" role="status">
              <p className="font-medium">Already billed on another invoice</p>
              <ul className="mt-1 list-disc pl-4">
                {lines
                  .filter((l) => l.billedOnInvoiceNumber)
                  .map((l) => (
                    <li key={l.id}>
                      {lineDisplayName(l)} — invoice #{l.billedOnInvoiceNumber}
                    </li>
                  ))}
              </ul>
              <p className="mt-1">These are left out when this quote is invoiced, so they&apos;re never billed twice.</p>
            </section>
          )}
          {/* Unresolved pricing (handoff W11) — product lines with no client
              price would reach the client as $0. Estimate only; a note, not a
              gate (Send/Convert rules are unchanged). */}
          {isEstimate && unpricedNames.length > 0 && (
            <section className="rounded-md bg-warn-bg px-[22px] py-4 text-sm text-warn" role="status">
              <p className="font-medium">
                {unpricedNames.length === 1
                  ? "1 item still needs a client price"
                  : `${unpricedNames.length} items still need a client price`}
              </p>
              <p className="mt-1">
                {unpricedNames.slice(0, 3).join(", ")}
                {unpricedNames.length > 3 ? ` and ${unpricedNames.length - 3} more` : ""} would
                show the client $0.
              </p>
            </section>
          )}
          {/* Estimate defaults (PR #154b cont.) — ESTIMATE-only (CEO 2026-07-24:
              never on a Quote), editable estimates only. */}
          {/* Sales tax (CEO 2026-10-13) — estimate AND quote, from the ship-to.
              No box (Stephanie 2026-10-14): it fills in on its own and shows
              on each item's Tax column and in the Client total. */}
          {canEdit && linesEditable && (
            <SalesTaxAuto
              orderId={order.id}
              ratePct={order.defaultTaxRate}
              shipToKey={
                order.shippingAddressSnapshot?.stateCode && order.shippingAddressSnapshot?.postalCode
                  ? `${order.shippingAddressSnapshot.stateCode} ${order.shippingAddressSnapshot.postalCode}`
                  : ""
              }
              hasPricedItems={lines.some(
                (l) =>
                  l.serviceType !== "shipping" &&
                  (Number(l.unitPrice) > 0 || l.variants.some((v) => Number(v.unitPrice ?? 0) > 0)),
              )}
              detailQueryKey={detailQueryKey}
            />
          )}
          {canEdit && linesEditable && order.statusStage === "quote" && (
            <EstimateDefaultsPanel
              defaultTaxRate={order.defaultTaxRate}
              defaultMarginPct={order.defaultMarginPct}
              saving={saveMutation.isPending}
              onSave={(patch) => saveMutation.mutate(patch)}
            />
          )}
          {!isEstimate && (
            <section className="rounded-md border border-rule bg-white p-[22px]">
              <h2 className="mb-2 text-lg font-semibold tracking-[-0.4px] text-ink">Shipping</h2>
              <p className="text-sm text-muted">
                {lines.some((l) => l.serviceType === "shipping")
                  ? "A shipping line is on this order. Re-estimate if the ship-to, quantities or items change."
                  : "Add a shipping estimate before sending the order."}
              </p>
              <p className="mt-1 text-xs text-muted">Supplier → decorator → client, FedEx or UPS ground at published rates.</p>
              {/* Internal notes (Stephanie 2026-10-14) — shown once Jake's DB
                  step has run (shippingNotes not null). Staff-only. */}
              {/* With a Shipping item on the quote the notes live on that item
                  (Stephanie 2026-10-27: "shipping is under the items"). */}
              {order.shippingNotes != null && !lines.some((l) => l.serviceType === "shipping") && (
                <ShippingNotesBox
                  key={order.id}
                  orderId={order.id}
                  notes={order.shippingNotes}
                  canEdit={canEdit}
                  onSaved={() => void queryClient.invalidateQueries({ queryKey: detailQueryKey })}
                />
              )}
              {linesEditable && !manualShipping && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowShippingEstimate(true)}>
                    <Truck className="mr-1 h-4 w-4" aria-hidden /> Estimate shipping
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setManualShipping({ description: "Shipping", cost: "", price: "", error: null })}
                  >
                    Enter manually
                  </Button>
                </div>
              )}
              {linesEditable && manualShipping && (
                <form
                  className="mt-4 space-y-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const m = manualShipping;
                    const cost = m.cost.trim().replace(/^\$/, "");
                    const price = m.price.trim().replace(/^\$/, "");
                    const err =
                      m.description.trim() === "" ? "Add a description"
                      : !/^\d+(\.\d{1,3})?$/.test(cost) ? "Add our cost — for example 45.00"
                      : !/^\d+(\.\d{1,2})?$/.test(price) ? "Add the client price — for example 60.00"
                      : null;
                    if (err) {
                      setManualShipping({ ...m, error: err });
                      return;
                    }
                    addManualShipping.mutate({ description: m.description.trim(), cost, price });
                  }}
                >
                  <label className="block text-sm">
                    <span className="mb-1 block text-xs text-muted">Description</span>
                    <Input
                      value={manualShipping.description}
                      onChange={(e) => setManualShipping({ ...manualShipping, description: e.target.value, error: null })}
                      maxLength={200}
                    />
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block text-sm">
                      <span className="mb-1 block text-xs text-muted">Our cost</span>
                      <Input
                        inputMode="decimal"
                        value={manualShipping.cost}
                        onChange={(e) => setManualShipping({ ...manualShipping, cost: e.target.value, error: null })}
                        placeholder="0.00"
                        autoFocus
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="mb-1 block text-xs text-muted">Client price</span>
                      <Input
                        inputMode="decimal"
                        value={manualShipping.price}
                        onChange={(e) => setManualShipping({ ...manualShipping, price: e.target.value, error: null })}
                        placeholder="0.00"
                      />
                    </label>
                  </div>
                  {manualShipping.error && <p className="text-sm text-risk" role="alert">{manualShipping.error}</p>}
                  <div className="flex flex-wrap items-center gap-3">
                    <Button type="submit" disabled={addManualShipping.isPending}>
                      {addManualShipping.isPending ? "Adding…" : "Add shipping"}
                    </Button>
                    <button
                      type="button"
                      onClick={() => setManualShipping(null)}
                      disabled={addManualShipping.isPending}
                      className="text-sm text-muted hover:text-ink"
                    >
                      Cancel
                    </button>
                  </div>
                  <p className="text-xs text-muted">Adds a shipping line to the quote. You can change it later in the line items.</p>
                </form>
              )}
            </section>
          )}
          <div ref={setTotalsEl} />
          {id && <SentEmailsCard doc="order" id={id} />}
        </aside>
        </div>
      </div>

      <p className="mt-8 text-xs text-muted">
        Last updated {timeAgo(order.updatedAt)}
      </p>

      {/* Clone target modal (PR #135) — client picker + new project +
          line selection. Source's client + project name pre-fill from
          the GET /orders/:id payload's `client` block. */}
      {showSentCopies && (
        <SentCopiesModal orderId={id} currency={order.currency ?? "USD"} onClose={() => setShowSentCopies(false)} />
      )}
      {showCloneModal && client && (
        <CloneTargetModal
          title={`Copy Order #${order.orderNumber}`}
          defaultClient={{ id: client.clientId, name: client.clientName }}
          defaultProjectName={`Copy of ${client.projectName ?? "project"}`}
          sourceItems={lines.map<CloneSourceItem>((l) => ({
            id: l.id,
            label:
              l.description && l.description.trim().length > 0
                ? l.description
                : `Line ${l.lineNumber}`,
          }))}
          submitting={cloneMutation.isPending}
          errorMessage={cloneError}
          onCancel={() => {
            if (cloneMutation.isPending) return;
            setShowCloneModal(false);
            setCloneError(null);
          }}
          onSubmit={(next) => cloneMutation.mutate(next)}
        />
      )}
      {showConvertModal && (
        <ConvertItemSelectionModal
          title={`Start the order for Quote #${order.orderNumber}`}
          ctaLabel="Start order"
          sourceItems={lines.map<ConvertSourceItem>((l) => ({
            id: l.id,
            // Variant-split lines are quantity-locked (their total is the sum
            // of the size/color breakdown) — the picker can drop them whole
            // but the quantity field is a no-op, so we say so in the label.
            name:
              (l.description && l.description.trim().length > 0
                ? l.description
                : l.productSnapshot?.name?.trim() || `Line ${l.lineNumber}`) +
              (l.variants.length > 0 ? " (sized — quantity locked)" : ""),
            imageUrl: convertLineImageUrl(l),
            defaultQty: l.quantityTotal > 0 ? l.quantityTotal : 1,
          }))}
          submitting={convertMutation.isPending}
          errorMessage={convertError}
          onCancel={() => {
            if (convertMutation.isPending) return;
            setShowConvertModal(false);
            setConvertError(null);
          }}
          onSubmit={(result: ConvertSelectionResult) => {
            setConvertError(null);
            convertMutation.mutate({
              line_selections: result.selectedItemIds.map((lineId) => ({
                line_id: lineId,
                quantity: result.itemQuantities[lineId] ?? 1,
              })),
            });
          }}
        />
      )}
      {showSendModal && client && (
        <SendToCustomerModal
          title={
            `Send Quote #${order.orderNumber}`
          }
          subtitle={
            order.statusStage === "quote"
              ? "Email the share link to the client and anyone you'd like to copy."
              : order.requiresUpfrontPayment
                ? "Email the Approve & Pay link to the client and anyone you'd like to copy."
                : "Email the approval link to the client and anyone you'd like to copy."
          }
          clientId={client.clientId}
          preview={
            order.publicShareUuid ? <PublicOrderPage previewUuid={order.publicShareUuid} /> : undefined
          }
          ctaLabel={
            missingLinkConfirmPending
              ? "Send anyway"
              : order.statusStage !== "quote" && order.requiresUpfrontPayment
                ? "Send & Request Payment"
                : "Send"
          }
          // Armed soft gate → rust, so the second click can't be muscle memory
          // on an identical peach button (PO $0-decoration precedent).
          ctaVariant={missingLinkConfirmPending ? "destructive" : "primary"}
          submitting={sendApprovalMutation.isPending}
          errorMessage={sendApprovalMsg}
          noticeMessage={sendModalNotice}
          showBodyEditor
          composeUrl={`/orders/${id}/send-compose`}
          bodyLabel="Email to the client"
          linkNoun={
            "quote link"
          }
          currentUserEmail={user?.email ?? null}
          showCopyToMe
          editableSubject
          extrasLabel="CC"
          onCancel={() => {
            if (sendApprovalMutation.isPending) return;
            setShowSendModal(false);
            setSendModalNotice(null);
            setMissingLinkConfirmPending(false);
          }}
          onSubmit={(recipients, options) => {
            setSendApprovalMsg(null);
            setNoContactPrompt(false);
            // A copy to yourself isn't a send to the client — an empty list
            // would otherwise fall back to the primary contact.
            if (recipients.length === 0 && options?.sendCopyToMe) {
              setSendApprovalMsg("Pick at least one client recipient — the copy to you goes along with it.");
              return;
            }
            sendApprovalMutation.mutate({
              recipients,
              bodyHtml: options?.bodyHtml,
              // Second click after the missing-link soft gate sends anyway.
              skipMissingLinkCheck: missingLinkConfirmPending,
              sendCopyToMe: options?.sendCopyToMe === true,
              subject: options?.subject,
            });
          }}
        />
      )}
    </div>
  );
}

// Product thumbnail extraction — mirrors the per-line table's snapImages
// logic so the picker rows read like the estimate + presentation.
function convertLineImageUrl(line: LineItem): string | null {
  const snapImages = line.productSnapshot?.imageUrls;
  const url = Array.isArray(snapImages)
    ? snapImages[0]
    : snapImages && typeof snapImages === "object"
      ? (Object.values(snapImages)[0] as string | undefined)
      : undefined;
  return url ?? null;
}

// File-local muted empty-value affordance (prescription #9, 2026-07-25). All
// `?? "—"` empties in this file render through this so a dash never reads as a
// real value. A shared component lands in a later round; kept local to avoid
// cross-file collisions with the sibling worker this round.
function Dash() {
  return <span className="text-muted">—</span>;
}

// Compact read-only header (CEO 2026-07-25). The old stacked `dl space-y-4` of
// five label/value rows (mostly em-dashes) ate ~a full viewport before the line
// items. This is a tight 2-up / 4-up meta strip. ALL FOUR pairs always render
// (UI review iter 1: layout stability wins — omitting Customer PO when null made
// every field jump a column when the rep clicked Edit, since EditableHeader
// always shows four fields); an empty Customer PO shows the muted <Dash/> like
// any other empty. The Introduction is a single wrapped row only when present,
// else an "Add introduction" ghost affordance (when editable) that opens the
// editor. Pattern: PublicOrderPage.tsx:499-509 (MetaRow grid).
function ReadOnlyHeader({
  order,
  showCustomerPo,
  canEdit,
  onAddIntroduction,
  onNotesSaved,
}: {
  order: OrderDetail;
  showCustomerPo: boolean;
  canEdit: boolean;
  onAddIntroduction: () => void;
  onNotesSaved: () => void;
}) {
  const hasIntro =
    order.introduction != null && order.introduction.trim() !== "";
  // The quote's own internal notes (Stephanie 2026-10-27). null = Jake's step
  // hasn't run, so the button isn't offered.
  const notesReady = order.internalNotes != null;
  const hasNotes = notesReady && (order.internalNotes ?? "").trim() !== "";
  const [notesOpen, setNotesOpen] = useState(false);
  return (
    <div className="space-y-4">
      {/* Stable four-slot grid — same slots as EditableHeader so nothing shifts
          when toggling edit mode. Odd-count trailing-gap on mobile is moot now
          that all four always render (a full 2×2 on grid-cols-2). */}
      {/* Customer PO only once it's a sales order / invoice — a quote doesn't
          need one (Stephanie 2026-10-26). */}
      <dl className={`grid grid-cols-2 gap-x-6 gap-y-4 ${showCustomerPo ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
        {showCustomerPo && <MetaPair label="Customer PO" value={order.customerPo} />}
        <MetaPair label="In-hands date" value={formatDate(order.inHandsDate)} />
        <MetaPair
          label="Terms"
          value={TERMS_LABELS[order.terms as TermsOption] ?? order.terms}
        />
        <MetaPair label="Currency" value={order.currency ?? "USD"} />
      </dl>
      {hasIntro ? (
        <div>
          <dt className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Introduction
          </dt>
          <dd className="mt-1 max-w-3xl whitespace-pre-line text-sm text-brown-500">
            {order.introduction}
          </dd>
        </div>
      ) : null}
      {(hasNotes || notesOpen) && (
        <div className="max-w-3xl">
          <ShippingNotesBox
            kind="internal"
            key={order.id}
            orderId={order.id}
            notes={order.internalNotes ?? ""}
            canEdit={canEdit}
            autoFocus={notesOpen && !hasNotes}
            onSaved={onNotesSaved}
          />
        </div>
      )}
      {canEdit && ((!hasIntro) || (notesReady && !hasNotes && !notesOpen)) && (
        <div className="flex flex-wrap items-center gap-5">
          {!hasIntro && (
            <button
              type="button"
              onClick={onAddIntroduction}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-brown-300 hover:text-rust-500"
            >
              <Plus className="h-3.5 w-3.5" /> Add introduction
            </button>
          )}
          {notesReady && !hasNotes && !notesOpen && (
            <button
              type="button"
              onClick={() => setNotesOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-brown-300 hover:text-rust-500"
            >
              <Plus className="h-3.5 w-3.5" /> Add internal notes
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// A small label/value pair in the meta strip. Empty / dash values render muted
// (prescription #9) so an absent value never masquerades as data.
// NOTE for the shared-primitive pass: this micro-label is text-xs (this file's
// convention). PublicOrderPage's MetaRow uses text-[10px] for the same role —
// the two should converge on one token when a shared MetaPair primitive lands.
// Do NOT retune PublicOrderPage here (sibling-owned this round).
function HeaderFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-ink">{value}</dd>
    </div>
  );
}

function MetaPair({ label, value }: { label: string; value: string | null }) {
  const empty = value == null || value.trim() === "" || value === "—";
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">
        {label}
      </dt>
      <dd className="mt-1 truncate text-sm font-medium text-ink">
        {empty ? <Dash /> : value}
      </dd>
    </div>
  );
}

function EditableHeader({
  form,
  showCustomerPo,
  currency,
  onChange,
  onCancel,
  onSave,
  saving,
  saveError,
}: {
  form: EditableFields;
  showCustomerPo: boolean;
  currency: string;
  onChange: (f: EditableFields) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  saveError: string | null;
}) {
  return (
    <div className="space-y-4 rounded-lg border border-peach-300 bg-peach-50/50 p-4">
      {/* Four header fields in a 4-up grid mirroring the read-only meta strip;
          the Introduction textarea spans full width below (CEO 2026-07-25). */}
      <div className={`grid grid-cols-1 gap-4 ${showCustomerPo ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
        {showCustomerPo && (
          <FormField label="Customer PO">
            <Input
              value={form.customerPo}
              onChange={(e) => onChange({ ...form, customerPo: e.target.value })}
              maxLength={120}
            />
          </FormField>
        )}
        <FormField label="In-hands date">
          <Input
            type="date"
            value={form.inHandsDate}
            onChange={(e) => onChange({ ...form, inHandsDate: e.target.value })}
          />
        </FormField>
        <FormField label="Terms">
          <select
            value={form.terms}
            onChange={(e) =>
              onChange({ ...form, terms: e.target.value as TermsOption })
            }
            className="w-full rounded-md border border-brown-200 bg-white px-3 py-2 text-sm text-brown-500 focus:border-peach-500 focus:outline-none focus:ring-1 focus:ring-peach-500"
          >
            {TERMS_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {TERMS_LABELS[t]}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Currency">
          <p className="px-1 py-2 text-sm text-brown-400">{currency}</p>
        </FormField>
      </div>
      <FormField label="Introduction">
        <textarea
          value={form.introduction}
          onChange={(e) => onChange({ ...form, introduction: e.target.value })}
          rows={4}
          className="w-full rounded-md border border-brown-200 bg-white px-3 py-2 text-sm text-brown-500 focus:border-peach-500 focus:outline-none focus:ring-1 focus:ring-peach-500"
        />
      </FormField>
      {saveError !== null && (
        <p className="text-xs text-rust-600">{saveError}</p>
      )}
      <div className="flex gap-2">
        <Button
          onClick={onSave}
          disabled={saving}
          size="sm"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button
          variant="ghost"
          onClick={onCancel}
          disabled={saving}
          size="sm"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

function FormField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        {label}
      </label>
      {children}
    </div>
  );
}

// 2026-05-26 (PR #153) — addresses panel. Operator-locked: "The estimates
// don't make sense - they need to be full estimates with totals (with a hidden
// totals checkbox) - full billing/shipping, etc." The totals themselves render
// through the single DocumentTotals block inside the lines card (CEO 2026-07-25
// removed the duplicate standalone totals panel that used to live here).

// 2026-07-20 — commonsku-style upfront payment (CEO spec + signed-off design).
// Rep sets the terms; the client approves + pays on /o/:uuid. Fields persist via
// PATCH /orders/:id (optimistic invalidate). No local mirror of the persisted
// booleans/radio — they render straight from the order so a failed PATCH reverts
// cleanly; only the partial-amount text input holds a draft (committed on blur).
function UpfrontPaymentPanel(props: {
  order: OrderDetail;
  currency: string;
  canEdit: boolean;
  orderSubtotal: string | null;
  saving: boolean;
  onSave: (patch: PatchBody) => void;
  onViewPayment?: () => void;
}) {
  const { order, currency, canEdit, orderSubtotal, saving, onSave, onViewPayment } =
    props;
  const required = order.requiresUpfrontPayment === true;
  const upfrontType: "full" | "partial" =
    order.upfrontType === "partial" ? "partial" : "full";
  const feeToClient = order.ccFeePaidBy === "client";
  const status = order.upfrontPaymentStatus ?? "not_required";
  const paid = status === "paid";
  const dueCents = order.upfrontDueCents ?? 0;
  const feeCents = order.ccFeeCents ?? 0;

  const [amountDraft, setAmountDraft] = useState<string>(order.upfrontAmount ?? "");
  useEffect(() => {
    setAmountDraft(order.upfrontAmount ?? "");
  }, [order.upfrontAmount]);

  // Default the partial base to 50% of the order subtotal when the rep first
  // switches to Partial (mirrors the design's "default 50% of total").
  const halfSubtotal = (): string => {
    const sub = Number(orderSubtotal ?? "0");
    return sub > 0 ? (Math.round(sub * 50) / 100).toFixed(2) : "";
  };

  const commitAmount = (): void => {
    const trimmed = amountDraft.trim();
    // Money format mirrors the API (numeric(12,2), non-negative) + > 0.
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed) || !(Number(trimmed) > 0)) {
      setAmountDraft(order.upfrontAmount ?? ""); // revert bad input
      return;
    }
    if (trimmed !== (order.upfrontAmount ?? "")) {
      onSave({ upfront_type: "partial", upfront_amount: trimmed });
    }
  };

  return (
    <div className="mt-6 rounded-md border border-rule bg-white">
      <div className="flex items-center justify-between px-5 pt-5 sm:px-[22px]">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
          Upfront payment
        </h2>
        {order.depositInvoiceId && onViewPayment && (
          <button
            type="button"
            onClick={onViewPayment}
            className="text-xs font-semibold text-ink hover:underline"
          >
            View payment
          </button>
        )}
      </div>

      <div className="space-y-3 px-5 pb-5 pt-3 sm:px-[22px]">
        <label className="flex min-h-[40px] items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={required}
            disabled={!canEdit || saving}
            onChange={(e) =>
              onSave(
                e.target.checked
                  ? { requires_upfront_payment: true, upfront_type: upfrontType }
                  : { requires_upfront_payment: false },
              )
            }
            className="h-4 w-4 rounded border-brown-200 text-rust-400 focus:ring-rust-300"
          />
          <span className="font-medium">Require upfront payment</span>
        </label>

        {required && (
          <>
            {/* Full / Partial */}
            <div className="flex flex-wrap items-center gap-4 pl-6">
              <label className="flex items-center gap-2 text-sm text-brown-500">
                <input
                  type="radio"
                  name="upfront-type"
                  checked={upfrontType === "full"}
                  disabled={!canEdit || saving}
                  onChange={() =>
                    onSave({ upfront_type: "full", upfront_amount: null })
                  }
                  className="h-4 w-4 border-brown-200 text-rust-400 focus:ring-rust-300"
                />
                <span>Full amount</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-brown-500">
                <input
                  type="radio"
                  name="upfront-type"
                  checked={upfrontType === "partial"}
                  disabled={!canEdit || saving}
                  onChange={() => {
                    const seed = halfSubtotal();
                    setAmountDraft(seed);
                    onSave({
                      upfront_type: "partial",
                      upfront_amount: seed !== "" ? seed : "0.01",
                    });
                  }}
                  className="h-4 w-4 border-brown-200 text-rust-400 focus:ring-rust-300"
                />
                <span>Partial</span>
              </label>
              {upfrontType === "partial" && (
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brown-400">
                  Amount
                  <input
                    inputMode="decimal"
                    value={amountDraft}
                    disabled={!canEdit || saving}
                    onChange={(e) => setAmountDraft(e.target.value)}
                    onBlur={commitAmount}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                    }}
                    placeholder="0.00"
                    className="w-28 rounded-lg border border-brown-200 bg-white px-3 py-1.5 text-right text-sm font-normal normal-case tabular-nums text-brown-500"
                  />
                </label>
              )}
            </div>

            {/* Card fee (3%) */}
            <label className="flex items-center gap-2 pl-6 text-sm text-brown-500">
              <input
                type="checkbox"
                checked={feeToClient}
                disabled={!canEdit || saving}
                onChange={(e) =>
                  onSave({
                    cc_fee_paid_by: e.target.checked ? "client" : "merchd",
                  })
                }
                className="h-4 w-4 rounded border-brown-200 text-rust-400 focus:ring-rust-300"
              />
              {/* Plain question, ticked = the client pays it (Stephanie
                  2026-10-26: the old wording read like a button). */}
              <span>Charge the client the {order.ccSurchargePct ?? 3}% card fee</span>
            </label>
            {/* Only when ticked — the unticked note ("other pays…") read as if
                someone else pays (Stephanie 2026-10-27). */}
            {feeToClient && (
              <p className="pl-12 text-xs text-muted">
                {feeCents > 0
                  ? `Card fee ${formatCurrency(feeCents / 100, currency)} is added to the upfront payment.`
                  : "No card fee is set up yet — ask Jake."}
              </p>
            )}

            {/* What the client pays + status banner */}
            <div className="pl-6 text-sm text-brown-400">
              Client pays{" "}
              <span className="font-semibold text-brown-500 tabular-nums">
                {formatCurrency(dueCents / 100, currency)}
              </span>
            </div>

            <div
              className={
                paid
                  ? "rounded-md bg-ok-bg px-4 py-2.5 text-sm font-medium text-ok"
                  : "rounded-md bg-warn-bg px-4 py-2.5 text-sm font-medium text-warn"
              }
            >
              {paid ? "Payment received" : "Payment not received"}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AddressesPanel(props: {
  billing: AddressSnapshot | null;
  shipping: AddressSnapshot | null;
  clientId: string | null;
  billingAddressId: string | null;
  shippingAddressId: string | null;
  canEdit: boolean;
  applying: boolean;
  /** Apply a chosen/saved address to the order (parent PATCHes the order). */
  onApply: (kind: "billing" | "shipping", addressId: string) => void;
}) {
  const {
    billing,
    shipping,
    clientId,
    billingAddressId,
    shippingAddressId,
    canEdit,
    applying,
    onApply,
  } = props;
  // Which block's editor is open. Editing needs a clientId to list the
  // client's saved addresses; without one the blocks are display-only.
  const [editing, setEditing] = useState<"billing" | "shipping" | null>(null);
  const editable = canEdit && clientId !== null;
  if (!billing && !shipping && !editable) return null;
  return (
    // id anchor: the "set it in Addresses" link in the NextActionCta's
    // no-ship-to create state scrolls here (same-tab #order-addresses).
    <div id="order-addresses" className="grid gap-4 md:grid-cols-2">
      <AddressCard
        label="Billing"
        address={billing}
        canEdit={editable}
        onEdit={() => setEditing("billing")}
      />
      <AddressCard
        label="Shipping"
        address={shipping}
        canEdit={editable}
        onEdit={() => setEditing("shipping")}
      />
      {editing !== null && clientId !== null && (
        <OrderAddressModal
          kind={editing}
          clientId={clientId}
          currentAddressId={
            editing === "billing" ? billingAddressId : shippingAddressId
          }
          applying={applying}
          onCancel={() => setEditing(null)}
          onApply={(addressId) => {
            onApply(editing, addressId);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/** Render the full address from the canonical snapshot keys (street1,
 *  stateCode, countryCode …) with a legacy fallback. Blank lines omitted. */
function AddressCard(props: {
  label: string;
  address: AddressSnapshot | null;
  canEdit?: boolean;
  onEdit?: () => void;
}) {
  const { label, address, canEdit, onEdit } = props;
  const company = address?.companyName ?? address?.company;
  const attention = address?.attention ?? address?.name;
  const street1 = address?.street1 ?? address?.line1;
  const street2 = address?.street2 ?? address?.line2;
  const city = address?.city;
  const state = address?.stateCode ?? address?.state;
  const postal = address?.postalCode;
  const country = address?.countryCode ?? address?.country;
  const cityLine = [[city, state].filter(Boolean).join(", "), postal].filter(Boolean).join(" ");
  const hasAny = Boolean(
    company || attention || street1 || street2 || cityLine,
  );
  const editAffordance =
    canEdit && onEdit ? "cursor-pointer hover:border-ink/40" : "";
  return (
    <div
      className={
        "group relative rounded-md border border-rule bg-white px-5 py-4 text-sm transition-colors sm:px-[22px] " +
        editAffordance
      }
      onClick={canEdit && onEdit ? onEdit : undefined}
      role={canEdit && onEdit ? "button" : undefined}
      tabIndex={canEdit && onEdit ? 0 : undefined}
      onKeyDown={
        canEdit && onEdit
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onEdit();
              }
            }
          : undefined
      }
    >
      <div className="mb-1.5 flex items-center justify-between">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
          {label}
        </h2>
        {canEdit && onEdit && (
          <span
            className="flex items-center gap-1 text-xs font-medium text-muted"
            aria-hidden="true"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </span>
        )}
      </div>
      {hasAny ? (
        <div className="space-y-0.5 text-brown-500">
          {company && <p className="font-medium">{String(company)}</p>}
          {attention && <p>Attn: {String(attention)}</p>}
          {street1 && <p>{String(street1)}</p>}
          {street2 && <p>{String(street2)}</p>}
          {cityLine && <p>{cityLine}</p>}
          {country && country !== "US" && <p>{String(country)}</p>}
          {address?.phone && (
            <p className="mt-1 text-xs text-brown-400">
              {String(address.phone)}
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs italic text-brown-300">
          {canEdit
            ? `Click to set a ${label.toLowerCase()} address.`
            : `No ${label.toLowerCase()} address on file.`}
        </p>
      )}
    </div>
  );
}

// Estimate defaults panel (PR #154b cont., 2026-06-01) — operator
// sets a per-estimate default tax rate + margin. Both opt-in; the
// percent inputs treat empty as "null" (clears the default). Tax
// drives the server-computed Tax totals row; margin auto-prices new
// product lines (AddProductModal reads it and seeds unit_price =
// net_cost / (1 - margin/100)).
function EstimateDefaultsPanel(props: {
  defaultTaxRate: string | null;
  defaultMarginPct: string | null;
  saving: boolean;
  onSave: (patch: PatchBody) => void;
}) {
  const { defaultTaxRate, defaultMarginPct, saving, onSave } = props;
  const [taxDraft, setTaxDraft] = useState<string>(defaultTaxRate ?? "");
  const [marginDraft, setMarginDraft] = useState<string>(defaultMarginPct ?? "");
  useEffect(() => {
    setTaxDraft(defaultTaxRate ?? "");
  }, [defaultTaxRate]);
  useEffect(() => {
    setMarginDraft(defaultMarginPct ?? "");
  }, [defaultMarginPct]);

  // Send a normalized 0..100 string on blur; "" clears. Reject NaN or
  // out-of-range so we don't paint an invalid value into the server.
  const commit = (field: "tax" | "margin", raw: string): void => {
    const trimmed = raw.trim();
    let normalized: string | null;
    if (trimmed.length === 0) {
      normalized = null;
    } else {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        // Snap back to the canonical server value; let the field show its
        // last accepted state.
        if (field === "tax") setTaxDraft(defaultTaxRate ?? "");
        else setMarginDraft(defaultMarginPct ?? "");
        return;
      }
      normalized = n.toFixed(2).replace(/\.?0+$/, "") || "0";
      // Re-format to 2dp for display.
      normalized = (Math.round(n * 100) / 100).toFixed(2);
    }
    if (field === "tax") {
      if (normalized === (defaultTaxRate ?? null)) return;
      onSave({ default_tax_rate: normalized });
    } else {
      if (normalized === (defaultMarginPct ?? null)) return;
      onSave({ default_margin_pct: normalized });
    }
  };

  return (
    <section className="rounded-md border border-rule bg-white p-5 sm:p-[22px]">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
          Quote defaults
        </h2>
        {saving && (
          <span className="text-xs text-muted">Saving…</span>
        )}
      </div>
      <p className="mt-1 text-xs text-muted">Internal — the client sees prices, not these settings.</p>
      <div className="mt-3 grid gap-4">
        {/* Tax moved to the Sales tax panel (worked out from the ship-to,
            CEO 2026-10-13). */}
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted">
            Default margin (%)
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={marginDraft}
            onChange={(e) => setMarginDraft(e.target.value)}
            onBlur={(e) => commit("margin", e.target.value)}
            disabled={saving}
            placeholder="40 (used when blank) → sell = cost / 0.60"
            className="h-10 rounded-md border border-rule bg-white px-3 text-sm text-ink focus:border-ink focus:outline-none disabled:bg-canvas"
          />
          <span className="text-xs text-brown-300">
            Auto-prices new product lines from net cost — 40% when this is
            blank. Doesn't retype already-added lines.
          </span>
        </label>
      </div>
    </section>
  );
}

function TermsAndConditionsPanel(props: {
  value: string | null;
  saving: boolean;
  onSave: (next: string | null) => void;
}) {
  const { value, saving, onSave } = props;
  const [draft, setDraft] = useState<string>(value ?? "");
  const [savedAt, setSavedAt] = useState<number | null>(null);
  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);
  // 2026-05-26 (PR #155) — save on blur, mirror the auto-save pattern
  // on the EditForm. Empty string → null clears the override (the
  // PublicOrderPage falls back to the hardcoded default).
  const commit = (): void => {
    const trimmed = draft.trim();
    const normalized = trimmed.length === 0 ? null : trimmed;
    if (normalized === (value ?? null)) return;
    onSave(normalized);
    setSavedAt(Date.now());
  };
  const status: string | null = saving
    ? "Saving…"
    : savedAt && Date.now() - savedAt < 2000
      ? "Saved"
      : null;
  return (
    <div className="mt-6 rounded-md border border-rule bg-white">
      <div className="flex items-baseline justify-between px-5 pt-5 sm:px-[22px]">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
          Terms &amp; conditions
        </h2>
        {status && (
          <span className="text-xs text-muted">{status}</span>
        )}
      </div>
      <p className="px-5 text-xs text-muted sm:px-[22px]">Client-facing — printed at the bottom of the document.</p>
      <div className="px-5 pb-5 pt-3 sm:px-[22px]">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          disabled={saving}
          rows={5}
          maxLength={20_000}
          placeholder="Leave blank to use the default terms, or paste custom terms for this document."
          className="w-full rounded-md border border-rule bg-white px-3 py-2 text-sm leading-relaxed text-ink focus:border-ink focus:outline-none disabled:bg-canvas"
        />
        <p className="mt-2 text-xs text-muted">
          Blank uses the default terms.
        </p>
      </div>
    </div>
  );
}

// 2026-05-28 — service_type enum → human label. There is no generic
// "service" value (the enum is product / shipping / setup_fee /
// decoration_service / rush / other_service), so the table maps each.
const SERVICE_TYPE_LABELS: Record<string, string> = {
  product: "Product",
  shipping: "Shipping",
  setup_fee: "Setup fee",
  decoration_service: "Decoration",
  rush: "Rush",
  other_service: "Service",
};

// Service-line subtype picker (2026-06-01, PR #154b cont.). The enum
// includes "product" too, but product lines come from the catalog
// modal — service rows are everything else.
type ServiceLineType =
  | "shipping"
  | "setup_fee"
  | "decoration_service"
  | "rush"
  | "other_service";

const SERVICE_LINE_TYPES: readonly ServiceLineType[] = [
  "shipping",
  "setup_fee",
  "decoration_service",
  "rush",
  "other_service",
];

// Sensible default description per type, used when the rep adds a fresh
// line and hasn't typed anything yet.
const SERVICE_LINE_DESCRIPTIONS: Record<ServiceLineType, string> = {
  shipping: "Shipping",
  setup_fee: "Setup fee",
  decoration_service: "Decoration",
  rush: "Rush charge",
  other_service: "New service",
};

// Prefer the product snapshot name (carried from the presentation on
// convert, or captured when added directly), then a free-text
// description, then a service-type label. Previously the table fell back
// straight to "(product)" because converted lines have a null description.
function lineDisplayName(l: LineItem): string {
  const snapName = l.productSnapshot?.name?.trim();
  if (snapName && snapName.length > 0) return snapName;
  const desc = l.description?.trim();
  if (desc && desc.length > 0) return desc;
  return SERVICE_TYPE_LABELS[l.serviceType] ?? "Line item";
}

// Estimate density toggle (2026-06-01, PR #154b cont.). Compact hides
// per-line variant tables for scanning; Detailed (today's behavior)
// shows them inline for editing. Persisted per-rep in localStorage so
// the same view re-appears on the next visit.
type EstimateDensity = "compact" | "detailed";

// Document vs Table view (CEO redesign 2026-07-19). "Document" is the
// commonsku-style itemised layout and is the DEFAULT — the estimate should read
// like the presentation it came from. "Table" keeps the dense spreadsheet for
// fast bulk editing, which reps still want; losing it would have been a
// regression dressed up as a redesign.
type DocumentView = "document" | "table";
const VIEW_STORAGE_KEY = "merchd.estimate.view";
function readStoredView(): DocumentView {
  if (typeof window === "undefined") return "document";
  const v = window.localStorage.getItem(VIEW_STORAGE_KEY);
  return v === "table" ? "table" : "document";
}
const DENSITY_STORAGE_KEY = "merchd.estimateDensity";

function readStoredDensity(): EstimateDensity {
  if (typeof window === "undefined") return "detailed";
  const v = window.localStorage.getItem(DENSITY_STORAGE_KEY);
  return v === "compact" ? "compact" : "detailed";
}

function LinesTable({
  lines,
  sections,
  currency,
  editable,
  orderId,
  projectId,
  detailQueryKey,
  taxCode,
  taxRatePct,
  itemCostsReady = false,
  hidePricing,
  onToggleHidePricing,
  onAddProductToSection,
  totalsTarget,
  cardFee = null,
  shippingNotes = null,
}: {
  /** The quote's internal shipping notes, shown on its Shipping item
   *  (Stephanie 2026-10-27). Null until Jake's step / when not loaded. */
  shippingNotes?: { notes: string; canEdit: boolean; onSaved: () => void } | null;
  /** The card fee the client pays on the upfront payment, shown in the
   *  Client total (Stephanie 2026-10-26). Null when there isn't one. */
  cardFee?: { cents: number; pct: number | null } | null;
  /** When set, the ONE totals block renders there (the right-hand Client
   *  total panel, handoff layout) instead of under the lines — moved, never
   *  duplicated (CEO 2026-07-25 one-totals rule). */
  totalsTarget?: HTMLElement | null;
  lines: LineItem[];
  /** Titled sections carried from the presentation. `[]` for every order
   *  created before sections existed — which is what makes this invisible
   *  to them. */
  sections: DocSection[];
  currency: string;
  editable: boolean;
  orderId: string;
  /** The owning project — threaded to OrderLineEditModal → ArtworkFilePicker. */
  projectId: string;
  detailQueryKey: readonly unknown[];
  /** Order-level tax code, shown against the tax row in the document footer. */
  taxCode?: string | null;
  /** Order-level tax rate percent (orders.default_tax_rate). */
  taxRatePct?: string | null;
  itemCostsReady?: boolean;
  /** orders.hide_pricing — the CEO's commonsku-style "Hide totals". */
  hidePricing?: boolean;
  onToggleHidePricing?: (next: boolean) => void;
  /** Opens the product picker with a category pre-targeted, so the added
   *  product lands under that heading. The modal lives on the page. */
  onAddProductToSection: (sectionId: string) => void;
}) {
  const queryClient = useQueryClient();
  // Invoices this quote's items can go onto (Stephanie 2026-10-19).
  const invoiceTargetsQ = useQuery({
    queryKey: ["orders", orderId, "invoice-targets"],
    queryFn: () => api<InvoiceTargets>(`/orders/${orderId}/invoice-targets`),
  });
  const [rowError, setRowError] = useState<string | null>(null);
  // Which product line's edit modal is open (unified tabbed editor, 2026-07-24).
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  // Which line's colour × size grid is open (document view).
  const [sizingLineId, setSizingLineId] = useState<string | null>(null);
  // An item with no colors yet shows the color picker straight away
  // (Stephanie 2026-10-26: "there shouldn't be a link you need to click");
  // Cancel hides it for that item.
  const [sizingDismissed, setSizingDismissed] = useState<ReadonlySet<string>>(new Set());
  // The colours / sizes each product comes in, from the catalogue. Drives the
  // grid's choices and the "sizes missing" warning (pilot 2026-10-13: POs
  // went to suppliers with no sizes).
  const { data: variantOptions } = useQuery({
    // Keyed on the product lines too, so a product added to the quote gets
    // its colours without waiting for the cache to age out.
    queryKey: [
      ...variantOptionsQueryKey(orderId),
      lines.map((l) => `${l.id}:${l.productId ?? ""}`).join(","),
    ],
    queryFn: () => fetchVariantOptions(orderId),
    enabled: editable,
    staleTime: 5 * 60_000,
  });
  const optionsByLine = useMemo(
    () => new Map((variantOptions ?? []).map((o) => [o.lineId, o])),
    [variantOptions],
  );
  const missingSizes = lines.filter(
    (l) =>
      l.serviceType === "product" &&
      l.variants.length === 0 &&
      optionsByLine.get(l.id)?.needsBreakdown === true,
  );
  const [view, setViewState] = useState<DocumentView>(readStoredView);
  const setView = (next: DocumentView): void => {
    setViewState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(VIEW_STORAGE_KEY, next);
    }
  };
  const [density, setDensityState] = useState<EstimateDensity>(readStoredDensity);
  const setDensity = (next: EstimateDensity): void => {
    setDensityState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, next);
    }
  };

  const patchMutation = useMutation({
    mutationFn: ({
      lineId,
      body,
    }: {
      lineId: string;
      body: Record<string, unknown>;
    }) =>
      api(`/orders/${orderId}/lines/${lineId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onError: (err) =>
      setRowError(
        err instanceof Error ? err.message : "Couldn't update the line.",
      ),
    onSuccess: () => {
      setRowError(null);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });

  // Per-colour price + quantity editing. This is NEW capability: the schema and
  // the PATCH endpoint have always accepted a per-variant unit_price, but no UI
  // ever wrote one, so a document could never show Blue at $4.98 and Dark Green
  // at $4.14. The document view is the first surface that can set it.
  const patchVariantMutation = useMutation({
    mutationFn: ({
      lineId,
      variantId,
      body,
    }: {
      lineId: string;
      variantId: string;
      body: Record<string, unknown>;
    }) =>
      api(`/orders/${orderId}/lines/${lineId}/variants/${variantId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onError: (err) =>
      setRowError(
        err instanceof Error ? err.message : "Couldn't update that color.",
      ),
    onSuccess: () => {
      setRowError(null);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (lineId: string) =>
      api(`/orders/${orderId}/lines/${lineId}`, { method: "DELETE" }),
    onError: (err) =>
      setRowError(
        err instanceof Error ? err.message : "Couldn't remove the line.",
      ),
    onSuccess: () => {
      setRowError(null);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });

  // ── Category (section) editing — CEO 2026-08-07 ──────────────────────────
  // All three lock on the SAME server guard as a line edit (ensureLinesEditable),
  // so grouping freezes when the money does.

  // A new category is created BLANK and named in place; this id makes the fresh
  // input take focus so the rep can just type.
  // Item move in progress, and the quote page's one price lock (on by default,
  // Stephanie 2026-10-27). Declared up here, before the "no items yet" return
  // below: adding the first item to an empty quote otherwise ran more hooks
  // than the render before and blanked the page (Stephanie 2026-11-02).
  const [moving, setMoving] = useState(false);
  const [retailLocked, setRetailLocked] = useState(true);
  const [freshSectionId, setFreshSectionId] = useState<string | null>(null);
  const [sectionNote, setSectionNote] = useState<string | null>(null);

  const createSection = useMutation({
    mutationFn: () =>
      api<{ section: { id: string } }>(`/orders/${orderId}/sections`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onError: (err) =>
      setRowError(
        err instanceof Error ? err.message : "Couldn't add the category.",
      ),
    onSuccess: (res) => {
      setRowError(null);
      setFreshSectionId(res.section.id);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });

  const renameSection = useMutation({
    mutationFn: (args: { id: string; title: string }) =>
      api(`/orders/${orderId}/sections/${args.id}`, {
        method: "PATCH",
        body: JSON.stringify({ title: args.title }),
      }),
    onError: (err) =>
      setRowError(
        err instanceof Error ? err.message : "Couldn't rename the category.",
      ),
    onSuccess: () => {
      setRowError(null);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });

  const removeSection = useMutation({
    mutationFn: (id: string) =>
      api<{ linesReleased: number }>(`/orders/${orderId}/sections/${id}`, {
        method: "DELETE",
      }),
    onError: (err) =>
      setRowError(
        err instanceof Error ? err.message : "Couldn't remove the category.",
      ),
    onSuccess: (res) => {
      // Say what happened to the PRODUCTS. "Removed" alone leaves the rep
      // wondering whether they just deleted billable work off a quote.
      setRowError(null);
      // Only name the caption when one will actually be on the page. The
      // "Also included" caption renders ONLY while the document still has a
      // category (sectionChromeFor → hasSections). Remove the last one and the
      // document reverts to plain unheaded lines — so telling the rep to look
      // for "Also included" would send them hunting for a label that is not
      // there, which is exactly the doubt the confirm step exists to remove.
      const captionWillExist = sections.length > 1;
      setSectionNote(
        res.linesReleased === 0
          ? "Category removed."
          : captionWillExist
            ? `Category removed — ${res.linesReleased} product${
                res.linesReleased === 1 ? "" : "s"
              } moved to \u201cAlso included\u201d.`
            : `Category removed — ${res.linesReleased} product${
                res.linesReleased === 1 ? "" : "s"
              } ${res.linesReleased === 1 ? "is" : "are"} ungrouped again.`,
      );
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });
  const sectionBusy =
    createSection.isPending ||
    renameSection.isPending ||
    removeSection.isPending;

  const busy = patchMutation.isPending || deleteMutation.isPending;

  if (lines.length === 0) {
    return (
      <div className="rounded-lg border border-brown-100 bg-white p-8 text-center text-sm text-brown-300">
        {editable
          ? "No line items yet. Use “Add product” above to start building this quote."
          : "No line items on this order."}
      </div>
    );
  }
  // Compact mode hides the per-line variant editor. We surface a small
  // "N variants" hint inside the line so reps know data is still there
  // and can flip Detailed to edit.
  const someLineHasVariants = lines.some((l) => l.variants.length > 0);

  // Titled grouping, carried from the presentation at convert. The SAME shared
  // function the client's copy uses (see the import comment) — so the rep and
  // the client cannot be looking at differently-grouped versions of one
  // document. Every line passed in comes back in exactly one group; that
  // totality is what the gate asserts, because "a product vanished off the
  // estimate" is the failure that matters.
  const groups = groupLinesForDocument(lines, sections, { editable });

  // Move an item up or down within its category (Stephanie 2026-10-27: "we
  // need to be able to move these around"). The whole document is renumbered
  // in the order shown, then only the lines whose position changed are saved.
  const moveLine = async (lineId: string, dir: -1 | 1) => {
    const order = groups.flatMap((g) => g.items.map((l) => l.id));
    const group = groups.find((g) => g.items.some((l) => l.id === lineId));
    if (!group) return;
    const ids = group.items.map((l) => l.id);
    const i = ids.indexOf(lineId);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    const a = order.indexOf(ids[i]!);
    const b = order.indexOf(ids[j]!);
    [order[a], order[b]] = [order[b]!, order[a]!];
    const posById = new Map(lines.map((l) => [l.id, l.position ?? 0]));
    setMoving(true);
    try {
      for (const [idx, id] of order.entries()) {
        if (posById.get(id) === idx) continue;
        await api(`/orders/${orderId}/lines/${id}`, { method: "PATCH", body: JSON.stringify({ position: idx }) });
      }
      setRowError(null);
    } catch (err) {
      setRowError(err instanceof Error ? err.message : "Couldn't move the item.");
    } finally {
      setMoving(false);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    }
  };
  // Whether the document uses the feature AT ALL. Drives the "Also included"
  // caption: an order with no sections must render exactly as it did before
  // sections existed, which is every order created before 2026-08-06.
  const hasSections = sections.length > 0;

  // Document footer figures, from the SAME shared integer-cents math the server
  // bills from (packages/shared) — so the on-screen total and the invoice can't
  // disagree. The server remains authoritative; this is the live view while the
  // rep edits.
  const moneyLines = lines.map((l) => ({
    unitPrice: l.unitPrice,
    netCost: l.netCost,
    quantityTotal: l.quantityTotal,
    serviceType: l.serviceType,
    variants: l.variants.map((v) => ({
      quantity: v.quantity,
      unitPrice: v.unitPrice,
      unitCost: v.unitCost,
    })),
    decorationCharges: (l.decorations ?? []).flatMap((d) =>
      (d.charges ?? []).map((c) => ({
        amount: c.amount,
        cost: c.cost,
        billingModel: c.billingModel,
        quantity: c.quantity ?? null,
      })),
    ),
  }));
  const documentTotals = computeOrderTotals(moneyLines, taxRatePct ?? null);
  // Shipping lines are inside the subtotal; the Client total panel shows them
  // on their own row (the subtotal there excludes them, so it still adds up).
  // Canonical per-line money (the same lineTotalCents the subtotal sums).
  const shippingLineCents = moneyLines.reduce(
    (n, ml, i) => n + (lines[i]?.serviceType === "shipping" ? lineTotalCents(ml) : 0),
    0,
  );
  // Booked margin — ALL-INCLUSIVE (CEO 2026-07-25): sell = goods + decoration/
  // additional charges + shipping line; cost = the same all-in cost via the SAME
  // shared math the commission booked-margin seed uses (lineAllInCostCents =
  // variant-aware goods cost + charge cost; shipping is its own line's netCost).
  // Keeping ONE definition of "booked margin" — all-inclusive — means the number
  // a rep sees on the document is the basis their commission is estimated on.
  // (Still LINE-derived + live here, vs the commission seed which reads the final
  // invoice; the shared piece is the DEFINITION of margin, not the source.)
  const allInSellCents = moneyLines.reduce((n, ml) => n + lineTotalCents(ml), 0);
  const allInCostCents = moneyLines.reduce((n, ml) => n + lineAllInCostCents(ml), 0);
  const bookedMarginCents = allInSellCents > 0 ? allInSellCents - allInCostCents : null;
  const bookedMarginPct =
    allInSellCents > 0 && allInCostCents > 0
      ? ((allInSellCents - allInCostCents) / allInSellCents) * 100
      : null;

  return (
    <div className="overflow-hidden rounded-lg border border-brown-100 bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-brown-50 bg-cream-50 px-4 py-2">
        {/* Segmented control per the canonical spec in AddProductModal.tsx
            (2026-07-30 harmonization round; backported 2026-07-30) — the
            active state moves from the old dark-brown fill to the canonical
            peach, with icon+label buttons. Behavior unchanged. */}
        <div
          role="group"
          aria-label="Document view"
          className="inline-flex overflow-hidden rounded-md border border-brown-50"
        >
          {(
            [
              { v: "document", label: "Document", Icon: FileText },
              { v: "table", label: "Table", Icon: Table2 },
            ] as const
          ).map(({ v, label, Icon }) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              aria-pressed={view === v}
              className={
                "inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold " +
                (view === v
                  ? "bg-peach-400 text-white"
                  : "text-brown-400 hover:bg-cream-100")
              }
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
        <span className="text-xs text-brown-300">
          {/* Shipping isn't an item or a unit (Stephanie 2026-10-26). */}
          {(() => {
            const goods = lines.filter((l) => l.serviceType !== "shipping");
            const units = goods.reduce((n, l) => n + l.quantityTotal, 0);
            return `${goods.length} item${goods.length === 1 ? "" : "s"} · ${units.toLocaleString()} ${units === 1 ? "unit" : "units"}`;
          })()}
        </span>
        {/* + Category (CEO 2026-08-07). Document view only — the table view
            renders categories as read-only bands, and an editor that only
            exists behind a view toggle is an editor half the reps never find. */}
        {editable && view === "document" && (
          <button
            type="button"
            onClick={() => createSection.mutate()}
            disabled={sectionBusy}
            className="rounded-md px-2 py-1 text-xs font-semibold text-brown-400 hover:bg-cream-100 hover:text-brown-600 disabled:opacity-50"
          >
            + Category
          </button>
        )}
        {/* Density only affects the dense table view. */}
        {view === "table" && someLineHasVariants && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              Density
            </span>
            {/* Same canonical segmented-control spec as the Document-view
                toggle above (AddProductModal.tsx, 2026-07-30). */}
            <div
              role="group"
              aria-label="Lines density"
              className="inline-flex overflow-hidden rounded-md border border-brown-50"
            >
              {(
                [
                  { d: "compact", label: "Compact", Icon: Rows4 },
                  { d: "detailed", label: "Detailed", Icon: Rows2 },
                ] as const
              ).map(({ d, label, Icon }) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDensity(d)}
                  aria-pressed={density === d}
                  className={
                    "inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold " +
                    (density === d
                      ? "bg-peach-400 text-white"
                      : "text-brown-400 hover:bg-cream-100")
                  }
                >
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {editable && missingSizes.length > 0 && (
        <div className="border-b border-rule bg-risk-bg px-4 py-2 text-sm text-risk">
          <span className="font-semibold">
            {missingSizes.length === 1 ? "1 item has" : `${missingSizes.length} items have`} no colors or sizes yet
          </span>{" "}
          — POs can't be created until they're added:{" "}
          {missingSizes.map((l, i) => (
            <Fragment key={l.id}>
              {i > 0 && ", "}
              <button
                type="button"
                onClick={() => {
                  setView("document");
                  setSizingLineId(l.id);
                  document
                    .getElementById(`order-line-${l.id}`)
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                className="font-semibold underline underline-offset-2 hover:text-ink"
              >
                {l.productSnapshot?.name ?? l.description ?? `Line ${l.lineNumber}`}
              </button>
            </Fragment>
          ))}
        </div>
      )}
      {rowError && (
        <p className="border-b border-rust-100 bg-rust-50 px-4 py-2 text-sm text-rust-500">
          {rowError}
        </p>
      )}
      {/* Category outcome — SUCCESS, so peach not rust. Removing a category is
          the one action that looks destructive and isn't; this line is where the
          rep learns their products survived and where they went. */}
      {sectionNote && (
        <p className="flex items-center justify-between gap-3 border-b border-peach-300 bg-peach-100 px-4 py-2 text-sm font-medium text-brown-500">
          <span>{sectionNote}</span>
          <button
            type="button"
            onClick={() => setSectionNote(null)}
            className="shrink-0 text-xs font-semibold text-brown-400 hover:text-rust-500"
          >
            Dismiss
          </button>
        </p>
      )}
      {view === "document" ? (
        <>
          {/* No column header here — each item card carries its own labels
              above its pricing (Stephanie 2026-10-26). */}
          {groups.map((group, groupIndex) => {
            // ONE decision, shared with the table view below and with the
            // client's /o/:uuid page. See sectionChromeFor — it is in
            // @merchd/shared so the gate can execute it rather than the three
            // surfaces each carrying their own copy of the condition.
            const chrome = sectionChromeFor(group, hasSections);
            return (
              <div key={group.key}>
                {/* px-4 aligns the heading with DocumentLineCard's own px-4
                    content edge. */}
                {/* A real category the rep can edit. Keyed off the group being
                    a REAL section (not the synthetic __ungrouped / __orphans
                    buckets) rather than off `chrome`: a freshly-created category
                    is BLANK, and the shared decision correctly hides a blank
                    heading from a client — but the rep must still see it to name
                    it. That is the whole reason groupLinesForDocument takes
                    `editable`. */}
                {editable && group.section !== null ? (
                  <div className="px-4">
                    <DocumentSectionEditHeader
                      title={group.section.title}
                      itemCount={group.items.length}
                      first={groupIndex === 0}
                      busy={sectionBusy}
                      autoFocus={freshSectionId === group.key}
                      onRename={(title: string) =>
                        renameSection.mutate({ id: group.key, title })
                      }
                      onAddProduct={() => onAddProductToSection(group.key)}
                      onDelete={() => removeSection.mutate(group.key)}
                    />
                  </div>
                ) : (
                  chrome.kind !== "none" && (
                    <div className="px-4">
                      {chrome.kind === "heading" ? (
                        <DocumentSectionHeading
                          title={chrome.title}
                          first={groupIndex === 0}
                        />
                      ) : chrome.kind === "blank-heading" ? (
                        // Unnamed category, read-only view. Without this branch
                        // it fell through to "Also included", which means
                        // something different: no category at all.
                        <DocumentSectionHeading
                          title=""
                          blank
                          first={groupIndex === 0}
                        />
                      ) : (
                        <DocumentSectionCaption first={groupIndex === 0} />
                      )}
                    </div>
                  )
                )}
                {group.items.map((l, itemIndex) => (
                  // Anchor target (2026-07-26): the create-PO "vendor missing"
                  // reason deep-links here (#order-line-<id>) so the rep lands on
                  // the exact line whose product must be re-selected — the
                  // round-35 edit-product pencil below is the concrete remedy.
                  <div id={`order-line-${l.id}`} key={l.id}>
                    <DocumentLineCard
                      line={l}
                      currency={currency}
                      showMoney
                      showRepDetail
                      retailLocked={retailLocked}
                      {...(editable ? { onToggleRetailLock: () => setRetailLocked((v) => !v) } : {})}
                      badge={
                        (l as LineItem).billedOnInvoiceNumber ? (
                          <span className="inline-block rounded bg-ok-bg px-1.5 py-0.5 text-[12px] font-semibold text-ok">
                            Billed on invoice #{(l as LineItem).billedOnInvoiceNumber} — not billed again on this quote
                          </span>
                        ) : undefined
                      }
                      taxRatePct={taxRatePct ?? null}
                      {...(editable && l.serviceType === "product" ? { onOpen: () => setEditingLineId(l.id) } : {})}
                      editable={editable}
                      onPatchLine={(body) =>
                        patchMutation.mutate({ lineId: l.id, body })
                      }
                      onPatchVariant={(variantId, body) =>
                        patchVariantMutation.mutate({ lineId: l.id, variantId, body })
                      }
                      belowQuantities={
                        l.serviceType === "shipping" && shippingNotes ? (
                          <ShippingNotesBox
                            key={orderId}
                            orderId={orderId}
                            notes={shippingNotes.notes}
                            canEdit={shippingNotes.canEdit}
                            onSaved={shippingNotes.onSaved}
                          />
                        ) : editable && l.serviceType === "product" ? (
                          sizingLineId === l.id || (l.variants.length === 0 && !sizingDismissed.has(l.id)) ? (
                            <SizeBreakdownEditor
                              orderId={orderId}
                              lineItemId={l.id}
                              variants={l.variants}
                              colours={optionsByLine.get(l.id)?.colours ?? []}
                              detailQueryKey={detailQueryKey}
                              onClose={() => {
                                setSizingLineId(null);
                                setSizingDismissed((s) => new Set(s).add(l.id));
                              }}
                              onStartEditing={() => setSizingLineId(l.id)}
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setSizingLineId(l.id);
                                setSizingDismissed((s) => { const n = new Set(s); n.delete(l.id); return n; });
                              }}
                              className={
                                "mt-1.5 text-xs font-semibold underline-offset-2 hover:underline " +
                                (missingSizes.some((m) => m.id === l.id)
                                  ? "text-risk"
                                  : "text-brown-400 hover:text-ink")
                              }
                            >
                              {l.variants.length > 0
                                ? "Edit colors & sizes"
                                : "+ Add colors & sizes"}
                            </button>
                          )
                        ) : undefined
                      }
                      actions={
                        editable || invoiceChoicesFor(invoiceTargetsQ.data, l) || removeFromInvoiceFor(invoiceTargetsQ.data, l) ? (
                          <div className="flex items-center gap-2">
                            {/* Put this item on an invoice that already exists
                                (Stephanie 2026-10-19: on the quote, per item). */}
                            <CopyToInvoiceButton
                              targets={invoiceTargetsQ.data}
                              line={l}
                              onDone={() => {
                                void queryClient.invalidateQueries({ queryKey: detailQueryKey });
                                void invoiceTargetsQ.refetch();
                              }}
                            />
                            {/* Invoiced quote: take an item off the quote AND
                                its invoice (Stephanie 2026-10-29, e.g. out of
                                stock) — the only way in while lines are locked. */}
                            <RemoveFromInvoiceButton
                              orderId={orderId}
                              targets={invoiceTargetsQ.data}
                              line={{ id: l.id, name: l.productSnapshot?.name ?? l.description ?? "Item" }}
                              onDone={() => {
                                void queryClient.invalidateQueries({ queryKey: detailQueryKey });
                                void queryClient.invalidateQueries({ queryKey: ["invoices"] });
                                void invoiceTargetsQ.refetch();
                              }}
                            />
                            {/* No pencil: clicking anywhere on the item opens it
                                (onOpen above, same editable + product-line gates)
                                — Stephanie 2026-10-14: "why is there still an
                                edit button". */}
                            {editable && group.items.length > 1 && (
                              <>
                                <button
                                  type="button"
                                  disabled={moving || itemIndex === 0}
                                  onClick={() => void moveLine(l.id, -1)}
                                  className="text-brown-300 hover:text-ink disabled:opacity-30"
                                  aria-label="Move up"
                                  title="Move up"
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  disabled={moving || itemIndex === group.items.length - 1}
                                  onClick={() => void moveLine(l.id, 1)}
                                  className="text-brown-300 hover:text-ink disabled:opacity-30"
                                  aria-label="Move down"
                                  title="Move down"
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            {editable && (
                              <button
                                type="button"
                                onClick={() => deleteMutation.mutate(l.id)}
                                className="text-brown-300 hover:text-rust-500"
                                aria-label={`Remove line ${l.lineNumber}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ) : undefined
                      }
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </>
      ) : (
      <table className="w-full">
        <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
          <tr>
            <th className="px-4 py-3">#</th>
            <th className="px-4 py-3">Description</th>
            <th className="px-4 py-3 text-right">Qty</th>
            <th className="px-4 py-3 text-right">Unit price</th>
            <th className="px-4 py-3 text-right">Line total</th>
            {editable && <th className="px-4 py-3 text-right" aria-label="Actions" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-brown-50">
          {groups.map((group, groupIndex) => {
            // The SAME decision object the document view above uses — not a
            // second copy of the condition. Two views of one document that
            // disagree about what is grouped is the failure this prevents.
            const chrome = sectionChromeFor(group, hasSections);
            // The table view is READ-ONLY, so it follows the client rule: never
            // a band over zero rows. The document view keeps empty categories
            // on purpose (that is where a rep names a brand-new one), and the
            // two views share one `groups` array — so the guard belongs here,
            // not in the grouping.
            if (group.items.length === 0) return null;
            return (
              <Fragment key={group.key}>
                {chrome.kind !== "none" && (
                  <DocumentSectionBandRow
                    title={chrome.kind === "heading" ? chrome.title : null}
                    blank={chrome.kind === "blank-heading"}
                    colSpan={editable ? 6 : 5}
                    first={groupIndex === 0}
                  />
                )}
                {group.items.map((l) => {
                  const hasVariants = l.variants.length > 0;
                  return (
                    <Fragment key={l.id}>
                      <OrderLineRow
                        line={l}
                        anchorId={`order-line-${l.id}`}
                        currency={currency}
                        editable={editable}
                        busy={busy}
                        hasVariants={hasVariants}
                        onPatch={(body) =>
                          patchMutation.mutate({ lineId: l.id, body })
                        }
                        onRemove={() => deleteMutation.mutate(l.id)}
                      />
                      {/* When a line carries per-variant rows (color/size grid),
                          render the editor right below the line. The variant
                          editor mutates qty server-side and rolls up
                          line.quantityTotal — see services/orders-builder.ts
                          rollupQuantityTotal. Compact density hides the
                          editor for scanning; rep flips to Detailed to edit. */}
                      {density === "detailed" &&
                        (hasVariants || editable) &&
                        l.serviceType === "product" && (
                          <VariantTableRow
                            orderId={orderId}
                            lineItemId={l.id}
                            variants={l.variants}
                            detailQueryKey={detailQueryKey}
                            colSpan={editable ? 6 : 5}
                            editable={editable}
                            colours={optionsByLine.get(l.id)?.colours}
                          />
                        )}
                      {/* Per-line Bills disclosure (PR #154b follow-up,
                          2026-06-01). Service lines (Shipping / Setup fee /
                          etc.) can carry vendor bills directly so the
                          line's real margin can be computed. Always shown
                          when editable; otherwise only when bills exist
                          (avoided here by always rendering — the inner
                          component renders a slim empty state). */}
                      {density === "detailed" &&
                        l.serviceType !== "product" &&
                        editable && (
                          <LineBillsSection
                            orderId={orderId}
                            lineItemId={l.id}
                            detailQueryKey={detailQueryKey}
                            colSpan={editable ? 6 : 5}
                            editable={editable}
                          />
                        )}
                    </Fragment>
                  );
                })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      )}
      {/* The ONE totals block (CEO 2026-07-25). Rendered OUTSIDE the
          document/table switch so both views footer with the same figures +
          the estimate-only Hide-totals control — no second/duplicate totals
          panel anywhere on the page. */}
      {(() => {
        // ONE element: in the right-hand column (portal, "panel" variant with
        // the shipping row) when the page provides the slot, else inline.
        const block = (
          <DocumentTotals
            {...(onToggleHidePricing
              ? { hideTotals: hidePricing ?? false, onToggleHideTotals: onToggleHidePricing }
              : {})}
            {...(totalsTarget
              ? {
                  variant: "panel" as const,
                  shippingCents: shippingLineCents,
                  shippingMissing: !lines.some((l) => l.serviceType === "shipping"),
                }
              : {})}
            subtotalCents={documentTotals.subtotalCents}
            taxCents={documentTotals.taxCents}
            totalCents={documentTotals.totalCents}
            currency={currency}
            taxCode={taxCode ?? null}
            bookedMarginCents={bookedMarginCents}
            bookedMarginPct={bookedMarginPct}
            cardFee={cardFee}
          />
        );
        return totalsTarget ? createPortal(block, totalsTarget) : block;
      })()}
      {/* Unified per-line product editor (2026-07-24). Opened from the
          Edit-product affordance on a product line's card; only reachable
          while `editable`. Pricing tab reuses DocumentLineCard + the same
          patch mutations, so no money logic is duplicated here. */}
      {editingLineId !== null &&
        (() => {
          const l = lines.find((x) => x.id === editingLineId);
          if (!l) return null;
          return (
            <OrderLineEditModal
              orderId={orderId}
              projectId={projectId}
              line={l}
              currency={currency}
              taxRatePct={taxRatePct ?? null}
              colours={optionsByLine.get(l.id)?.colours ?? []}
              saleLines={optionsByLine.get(l.id)?.saleLines}
              itemCostsReady={itemCostsReady}
              detailQueryKey={detailQueryKey}
              onPatchLine={(body) =>
                patchMutation.mutate({ lineId: l.id, body })
              }
              onPatchVariant={(variantId, body) =>
                patchVariantMutation.mutate({ lineId: l.id, variantId, body })
              }
              onClose={() => setEditingLineId(null)}
            />
          );
        })()}
    </div>
  );
}

function OrderLineRow({
  line,
  anchorId,
  currency,
  editable,
  busy,
  hasVariants,
  onPatch,
  onRemove,
}: {
  line: LineItem;
  /** DOM id so the create-PO "vendor missing" reason can deep-link to this row. */
  anchorId?: string;
  currency: string;
  editable: boolean;
  busy: boolean;
  /** True when the line has per-color/size variant rows. The line-level
   *  qty input becomes a derived display (rep edits per-variant qty
   *  in the VariantTable below). */
  hasVariants: boolean;
  onPatch: (body: Record<string, unknown>) => void;
  onRemove: () => void;
}) {
  const isProduct = line.serviceType === "product";
  // Our SKU first, then the supplier part number reps buy by.
  const sku = [line.catalogueSku?.trim(), line.productSnapshot?.sku?.trim()].filter(Boolean).join(" · ");
  // Product thumbnail — same extraction the presentation + product-edit
  // modal use, so the Estimate / Quote read like the presentation.
  const snapImages = line.productSnapshot?.imageUrls;
  const imageUrl = Array.isArray(snapImages)
    ? snapImages[0]
    : snapImages && typeof snapImages === "object"
      ? (Object.values(snapImages)[0] as string | undefined)
      : undefined;
  const [qty, setQty] = useState(String(line.quantityTotal));
  const [price, setPrice] = useState(() => Number(line.unitPrice).toFixed(2));
  const [desc, setDesc] = useState(line.description ?? "");

  // Re-seed local inputs whenever a refetch changes the canonical values
  // (e.g. after another field's PATCH settles).
  useEffect(() => {
    setQty(String(line.quantityTotal));
  }, [line.quantityTotal]);
  useEffect(() => {
    setPrice(Number(line.unitPrice).toFixed(2));
  }, [line.unitPrice]);
  useEffect(() => {
    setDesc(line.description ?? "");
  }, [line.description]);

  const qtyNum = Math.max(0, Math.floor(Number(qty) || 0));
  const priceNum = Number(price);
  const total = Number.isFinite(qtyNum * priceNum) ? qtyNum * priceNum : 0;

  const commitQty = (): void => {
    const n = Math.max(0, Math.floor(Number(qty) || 0));
    setQty(String(n));
    if (n !== line.quantityTotal) onPatch({ quantity_total: n });
  };
  const commitPrice = (): void => {
    const n = Number(price);
    if (!Number.isFinite(n) || n < 0) {
      setPrice(Number(line.unitPrice).toFixed(2));
      return;
    }
    const formatted = n.toFixed(2);
    setPrice(formatted);
    if (formatted !== Number(line.unitPrice).toFixed(2)) {
      onPatch({ unit_price: formatted });
    }
  };
  const commitDesc = (): void => {
    const d = desc.trim();
    if (d !== (line.description ?? "")) {
      onPatch({ description: d.length > 0 ? d : null });
    }
  };

  const cellInput =
    "rounded-md border border-brown-100 bg-white px-2 py-1 text-sm text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200 disabled:opacity-50";

  return (
    <tr id={anchorId}>
      <td className="px-4 py-3 align-top text-sm text-brown-400">
        {line.lineNumber}
      </td>
      <td className="px-4 py-3 text-sm text-brown-500">
        {editable && !isProduct ? (
          // Service-line inline editor: type picker + description.
          // PATCHing service_type retypes the line so a row mis-added as
          // "Service" can be flipped to "Shipping" (and immediately
          // populates the /o/:uuid Shipping totals row).
          <div className="flex items-stretch gap-2">
            <select
              value={line.serviceType}
              onChange={(e) =>
                onPatch({ service_type: e.target.value })
              }
              disabled={busy}
              className="rounded-md border border-brown-100 bg-white px-2 py-1 text-xs font-semibold uppercase tracking-wide text-brown-400 hover:border-peach-400 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200 disabled:opacity-50"
              aria-label="Service type"
            >
              {SERVICE_LINE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {SERVICE_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            <input
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              onBlur={commitDesc}
              placeholder={SERVICE_TYPE_LABELS[line.serviceType] ?? "Description"}
              className={`flex-1 ${cellInput}`}
            />
          </div>
        ) : (
          <div className="flex items-start gap-3">
            {/* Product thumbnail — mirrors the presentation item card so the
                Estimate / Quote line items read the same. */}
            <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-brown-50 bg-cream-100">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[10px] uppercase tracking-wide text-brown-300">
                  No image
                </div>
              )}
            </div>
            <div className="min-w-0">
              <div className="font-medium">{lineDisplayName(line)}</div>
              {line.billedOnInvoiceNumber ? (
                <div className="mt-0.5 inline-block rounded bg-ok-bg px-1.5 text-[11px] font-semibold text-ok">
                  Billed on invoice #{line.billedOnInvoiceNumber}
                </div>
              ) : null}
              {sku && sku.length > 0 && (
                <div className="mt-0.5 text-xs text-brown-300">{sku}</div>
              )}
            </div>
          </div>
        )}
        {/* Per-variant rows now render in a dedicated VariantTableRow
            below this line; the prior condensed string is redundant. */}
        {line.decorations.length > 0 && (
          <div className="mt-1 text-xs text-brown-300">
            {line.decorations.length} decoration
            {line.decorations.length === 1 ? "" : "s"}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right align-top text-sm tabular-nums text-brown-500">
        {editable && !hasVariants ? (
          <input
            type="number"
            min={0}
            value={qty}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => setQty(e.target.value)}
            onBlur={commitQty}
            disabled={busy}
            className={`w-16 text-right tabular-nums ${cellInput}`}
          />
        ) : (
          <span
            className="tabular-nums"
            title={hasVariants ? "Derived from variant quantities" : undefined}
          >
            {qtyNum.toLocaleString()}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right align-top text-sm tabular-nums text-brown-500">
        {editable ? (
          <input
            type="text"
            inputMode="decimal"
            value={price}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => setPrice(e.target.value)}
            onBlur={commitPrice}
            disabled={busy}
            className={`w-24 text-right tabular-nums ${cellInput}`}
          />
        ) : (
          formatCurrency(priceNum, currency)
        )}
      </td>
      <td className="px-4 py-3 text-right align-top text-sm font-semibold tabular-nums text-brown-500">
        {formatCurrency(total, currency)}
      </td>
      {editable && (
        <td className="px-4 py-3 text-right align-top">
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            aria-label="Remove line"
            className="text-brown-300 transition-colors hover:text-rust-500 disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </td>
      )}
    </tr>
  );
}

function formatCurrency(amount: number, currency: string): string {
  if (!Number.isFinite(amount)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (y === undefined || m === undefined || d === undefined) return iso;
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const monthName = months[Number.parseInt(m, 10) - 1] ?? m;
  return `${monthName} ${Number.parseInt(d, 10)}, ${y}`;
}

/** "Can't find it? Add custom product" in the estimate / quote add window. */
function OrderCustomProduct(props: {
  onCreated: (p: CreatedCustomProduct) => Promise<string | null>;
  onItemAdded: () => void;
  addedText: string;
  addLabel: string;
}) {
  const [open, setOpen] = useState(false);
  return open ? (
    <CustomProductForm
      onCreated={props.onCreated}
      addedText={props.addedText}
      addLabel={props.addLabel}
      onItemAdded={props.onItemAdded}
      onClose={() => setOpen(false)}
    />
  ) : (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-muted"
    >
      <Plus className="h-4 w-4" />
      Can&apos;t find it? Add custom product
    </button>
  );
}

/** Internal shipping notes on the Shipping panel (Stephanie 2026-10-14):
 *  "a place for the rep to enter internal notes especially if there are
 *  multiple items". Saves when the box loses focus. Never on the client's copy. */
function ShippingNotesBox(props: {
  orderId: string;
  notes: string;
  canEdit: boolean;
  onSaved: () => void;
  /** Same box for the quote's own internal notes (Stephanie 2026-10-27). */
  kind?: "shipping" | "internal";
  autoFocus?: boolean;
}) {
  const { orderId, notes, canEdit, onSaved } = props;
  const internal = props.kind === "internal";
  const [draft, setDraft] = useState(notes);
  const [error, setError] = useState<string | null>(null);
  // "@" tags (Stephanie 2026-10-21): each person is told once, when the notes
  // that first tag them are saved.
  const [tagged, setTagged] = useState<string[]>([]);
  const [told, setTold] = useState<string[]>([]);
  useEffect(() => setDraft(notes), [notes]);
  const save = useMutation({
    mutationFn: (value: string) =>
      api<{ notes: string }>(`/orders/${orderId}/${internal ? "internal-notes" : "shipping-notes"}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes: value, mention_user_ids: tagged.filter((id) => !told.includes(id)) }),
      }),
    onSuccess: () => {
      setError(null);
      setTold((t) => [...new Set([...t, ...tagged])]);
      onSaved();
    },
    onError: () => setError("Couldn't save the notes. Try again."),
  });
  return (
    <label className={internal ? "block" : "mt-4 block"}>
      <span className="mb-1 block text-xs text-muted">
        {internal ? "Internal notes on this quote" : "Internal notes"} — the team only, never on the client&rsquo;s copy
      </span>
      <MentionTextarea
        rows={4}
        autoFocus={props.autoFocus}
        value={draft}
        maxLength={4000}
        disabled={!canEdit || save.isPending}
        onChange={(t, ids) => { setDraft(t); setTagged(ids); }}
        onBlur={() => {
          if (draft.trim() !== notes.trim()) save.mutate(draft);
        }}
        placeholder={
          internal
            ? "e.g. Client wants the navy, waiting on the logo file. Type @ to tag someone"
            : "e.g. Tees ship from SanMar to the decorator; mugs drop-ship to the client. Type @ to tag someone"
        }
        aria-label={internal ? "Internal notes on this quote" : "Internal shipping notes"}
        className="w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-ink focus:outline-none disabled:opacity-60"
      />
      {save.isPending ? (
        <span className="text-xs text-muted">Saving…</span>
      ) : error ? (
        <span role="alert" className="text-xs text-risk">{error}</span>
      ) : null}
    </label>
  );
}
